/**
 * VendorModal - service-aware vendor onboarding wizard.
 *
 * The wizard tests one vendor endpoint, then maps its response paths into the
 * canonical output fields expected by the selected service capability.
 */

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdapterServiceCatalog } from '../hooks/useAdapterServices';
import { ConfigLabelSelect } from './adapter/ConfigLabelSelect';
import {
  adapterEndpointServiceName,
  buildCatalogGroupsForLabel,
  catalogContextLabel,
  isAdapterBackedCapability,
  findCapabilityOptionByLabel,
  mergeAdapterCatalog,
  normalizeConfigLabel,
  normalizeRoutingSlug,
  resolveVendorAdapterConfigLabel,
  resolveVendorCatalogGroup,
  resolveVendorRoutingSlug,
  payloadPathToJsonata,
  runtimeRequestFieldsFromTargets,
  realisticSamplePayloadForCapability,
  samplePayloadForCapability,
  selectableCapabilitiesForGroup,
  vendorAddApiDisabledMessage,
  type CapabilityOptionLike,
} from '../lib/adapterCapabilityCatalog';
import {
  buildPreviewProviderContext,
  formatJsonataError,
  providerVariableRowsFromRecord,
  providerVariablesFromRows,
  substituteProviderVariables,
  validateProviderVariableKey,
  type ProviderVariableRow,
} from '../lib/providerVariables';
import {
  authProfileKindForCapability,
  defaultRuntimeMappingTemplateForStepKey,
  inferAuthTypeFromAuthConfig,
  mergeEffectiveAuthConfig,
  pickAuthConfigForProfile,
  resolveStepKey,
} from '../lib/authProfileKind';
import { removeEndpointMapping, findSavedEndpointMapping, isCapabilityClaimed, canAddApiForVendorGroup, unclaimedCapabilitiesForGroup } from '../lib/endpointMappingUtils';
import { LEGACY_CAPABILITY_CATALOG } from '../lib/legacyCapabilityCatalog';
import { CapabilityAuthConnectPanel } from './CapabilityAuthConnectPanel';
import { RequestJsonConfirmDialog } from './RequestJsonConfirmDialog';
import { collectPreviewSecretValues, maskRequestPreviewForDisplay } from '../lib/requestPreviewMasking';
import jsonata from 'jsonata';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendIcon from '@mui/icons-material/Send';

import { useCreateVendor, useUpdateVendor, useTestConnection, useLiveTest } from '../hooks/useVendors';
import { PermissionKey } from '@c4ytechnology/authz';
import { hasAdminPermission } from '../lib/adminPermissions';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';
import { liveTestAuthWarning, vendorTestErrorMessage } from '../lib/vendorTestErrors';
import {
  applyVendorModalDraft,
  buildDraftKey,
  clearDraftsForContext,
  clearDraftsForVendorId,
  clearVendorOnboardingDraft,
  effectiveDraftVendorId,
  loadVendorOnboardingDraft,
  findDraftForContext,
  hasMeaningfulProgress,
  saveVendorOnboardingDraft,
  type VendorOnboardingDraftSnapshot,
  type VendorOnboardingMode,
} from './vendorOnboardingWizardState';
import {
  dragPathForTreeRow,
  jsonataExpressionForPath,
  responseMappingExpressionForInput,
} from './vendorModalResponseMapping.js';
import type {
  AuthType,
  CreateVendorRequest,
  DoctrineId,
  FieldMapping,
  LiveTestResult,
  RequestBodyType,
  RoutingServiceKey,
  UpdateVendorRequest,
  VendorEndpointMapping,
  VendorMapping,
  VendorMappingSaveResponse,
} from '../domain/vendor';

interface HeaderRow { rowId: string; key: string; value: string; }
interface BodyBuilderRow { rowId: string; path: string; value: string; }
interface RequestMappingBuilderRow { rowId: string; path: string; expression: string; }
interface RequestMappingDisplayRow extends RequestMappingBuilderRow { fromBody: boolean; mapped: boolean; }

interface RequestMappingValidation {
  ok: boolean;
  pending?: boolean;
  error?: string;
  preview?: unknown;
}

interface RuntimeRequestField {
  path: string;
  label: string;
  description: string;
  required?: boolean;
}

type CapabilityOption = CapabilityOptionLike & {
  mappingTargets: CanonicalField[];
  requestMappingTargets?: CanonicalField[];
};

interface CanonicalField {
  name: string;
  description: string;
  required?: boolean;
}

const STEPS = ['Connect', 'Request', 'Live Test', 'Response Mapping'];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const REQUEST_MAPPING_PANEL_HEIGHT = 420;
const REQUEST_PREVIEW_EDITOR_HEIGHT = '180px';
const RESPONSE_MAPPING_PANEL_HEIGHT = 480;
const RESPONSE_TREE_GRID_COLUMNS = 'minmax(140px, auto) minmax(120px, auto) minmax(180px, 1fr)';

function adaptiveScrollPanelSx(maxHeight: number, minHeight = 160) {
  return {
    minHeight,
    maxHeight,
    overflow: 'auto' as const,
    flex: 1,
    flexShrink: 0,
  };
}

const RESPONSE_PANEL_SHELL_HEIGHT = RESPONSE_MAPPING_PANEL_HEIGHT + 68;
const RESPONSE_SECTION_HEADER_MIN_HEIGHT = 48;

function responseFieldDescriptionHeading(fields: { description?: string }[]): string | null {
  const descriptions = [...new Set(fields.map(f => f.description?.trim()).filter(Boolean))];
  return descriptions.length === 1 ? descriptions[0]! : null;
}

function responseFieldPurposeLabel(description: string): string {
  if (description.includes('Normalized response key')) {
    return 'Each row is a canonical output key your adapter publishes. Map a vendor JSON path here to populate that key in the normalized response.';
  }
  return description;
}

const COMPACT_SELECT_SX = {
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} as const;

function uid(): string {
  return Math.random().toString(36).slice(2, 9);
}

function slugVendorName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function vendorDisplayName(vendor: VendorMapping | null): string {
  return vendor?.vendorName?.trim() || vendor?.vendorId || '';
}

function saveDoctrineFields(
  useCatalog: boolean,
  existingVendor?: VendorMapping | null,
): { doctrineId?: DoctrineId } {
  if (useCatalog) return {};
  return existingVendor?.doctrineId ? { doctrineId: existingVendor.doctrineId } : {};
}

function selectableCapabilities(
  catalog: CapabilityOption[],
  serviceName: RoutingServiceKey,
  groupKey: string | null,
): CapabilityOption[] {
  return selectableCapabilitiesForGroup(catalog, serviceName, groupKey) as CapabilityOption[];
}

function firstSelectableCapability(
  catalog: CapabilityOption[],
  serviceName: RoutingServiceKey,
  groupKey: string | null,
): CapabilityOption {
  return selectableCapabilities(catalog, serviceName, groupKey)[0] ?? catalog[0];
}

function firstUnclaimedCapability(
  catalog: CapabilityOption[],
  serviceName: RoutingServiceKey,
  groupKey: string | null,
  mappings: VendorEndpointMapping[],
): CapabilityOption {
  const pool = selectableCapabilities(catalog, serviceName, groupKey);
  for (const option of pool) {
    const adapterKey = adapterEndpointServiceName(option);
    if (!isCapabilityClaimed(mappings, option.capability, serviceName, adapterKey)) {
      return option;
    }
  }
  return firstSelectableCapability(catalog, serviceName, groupKey);
}

function capabilityForSelection(
  catalog: CapabilityOption[],
  serviceName: RoutingServiceKey,
  groupKey: string | null,
  capability: unknown,
): CapabilityOption {
  const pool = selectableCapabilities(catalog, serviceName, groupKey);
  if (typeof capability === 'string') {
    const found = pool.find(c => c.capability === capability);
    if (found) return found;
    if (!groupKey) {
      const anywhere = catalog.find(c => c.capability === capability);
      if (anywhere) return anywhere;
    }
  }
  return firstSelectableCapability(catalog, serviceName, groupKey);
}

const MASKED_SECRET_VALUE = '__EVO_VENDOR_SECRET_MASKED__';
const SECRET_DISPLAY_VALUE = '********';
const MITEK_DEFAULT_SCOPE = 'global.identity.api dossier.creator';

function defaultRuntimeSamplePayloadText(
  option: CapabilityOption | undefined,
  providerVariables: Record<string, string> = {},
): string {
  return JSON.stringify(
    realisticSamplePayloadForCapability(option, providerVariables),
    null,
    2,
  );
}

function samplePayloadVars(rows: ProviderVariableRow[]): Record<string, string> {
  return providerVariablesFromRows(rows);
}

function runtimeField(
  path: string,
  label: string,
  description: string,
  required?: boolean,
): RuntimeRequestField {
  return required === true
    ? { path, label, description, required: true }
    : { path, label, description };
}

function runtimeRequestFieldsForCapability(option: CapabilityOption | undefined): RuntimeRequestField[] {
  const fromCatalog = runtimeRequestFieldsFromTargets(option?.requestMappingTargets);
  if (fromCatalog.length > 0) return fromCatalog;
  if (option && isAdapterBackedCapability(option.capability)) return [];
  return Array.from(new Set(flattenPaths(samplePayloadForCapability(option))))
    .sort()
    .map(path => runtimeField(path, path, 'Available from the runtime adapter payload'));
}

function isMaskedSecretValue(value: unknown): boolean {
  return value === MASKED_SECRET_VALUE;
}

function secretInputValue(value: string | undefined): string {
  return isMaskedSecretValue(value) ? SECRET_DISPLAY_VALUE : value ?? '';
}

function secretInputType(value: string | undefined): 'text' | 'password' {
  return isMaskedSecretValue(value) ? 'text' : 'password';
}

function secretAuthValue(authConfig: Record<string, string>, field: string, fallback = ''): string {
  const value = authConfig[field];
  return value && !isMaskedSecretValue(value) ? value : fallback;
}

function buildAuthHeaders(
  authType: AuthType,
  authConfig: Record<string, string>,
): Record<string, string> {
  switch (authType) {
    case 'api-key': {
      const apiKey = authConfig.apiKey ?? authConfig.key;
      return authConfig.headerName && apiKey && !isMaskedSecretValue(apiKey)
        ? { [authConfig.headerName]: apiKey }
        : {};
    }
    case 'bearer':
      return authConfig.token && !isMaskedSecretValue(authConfig.token)
        ? { Authorization: `Bearer ${authConfig.token}` }
        : {};
    case 'basic': {
      if (!authConfig.username || !authConfig.password || isMaskedSecretValue(authConfig.password)) return {};
      return { Authorization: `Basic ${btoa(`${authConfig.username}:${authConfig.password}`)}` };
    }
    case 'oauth-client-credentials':
      return {};
    default:
      return {};
  }
}

function joinUrl(baseUrl: string, path: string): string {
  const trimmedPath = path.trim();
  if (/^https?:\/\//i.test(trimmedPath)) return trimmedPath;
  const base = baseUrl.replace(/\/+$/, '');
  const suffix = trimmedPath.replace(/^\/+/, '');
  return suffix ? `${base}/${suffix}` : base;
}

function incomeFlinksTokenTestUrl(baseUrl: string, authConfig: Record<string, string>): string {
  return joinUrl(
    baseUrl,
    authConfig.authorizeTokenUrl ||
      authConfig.tokenUrl ||
      authConfig.authorizeTokenPath ||
      authConfig.tokenPath ||
      '',
  );
}

function parseBooleanConfig(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false;
  return undefined;
}

function isTokenPreflightEnabled(authConfig: Record<string, string>): boolean {
  const explicit = parseBooleanConfig(
    authConfig.tokenPreflightEnabled ??
      authConfig.useTokenPreflight ??
      authConfig.enableTokenPreflight,
  );
  if (explicit !== undefined) return explicit;
  return Boolean(
    authConfig.authorizeTokenUrl ||
      authConfig.tokenUrl ||
      authConfig.authorizeTokenPath ||
      authConfig.tokenPath,
  );
}

function hasMaskedAuthSecret(authType: AuthType, authConfig: Record<string, string>): boolean {
  if (authType === 'api-key') {
    return isMaskedSecretValue(authConfig.apiKey) ||
      isMaskedSecretValue(authConfig.key) ||
      isMaskedSecretValue(authConfig.accountsApiKey) ||
      isMaskedSecretValue(authConfig.flinksApiKey);
  }
  if (authType === 'bearer') return isMaskedSecretValue(authConfig.token);
  if (authType === 'basic') return isMaskedSecretValue(authConfig.password);
  if (authType === 'oauth-client-credentials') return isMaskedSecretValue(authConfig.clientSecret);
  return false;
}

function hasNonEmptyEnvName(value: string | undefined): boolean {
  return Boolean(value?.trim());
}

function hasUsableAuthConfig(authType: AuthType, authConfig: Record<string, string>): boolean {
  if (authType === 'none') return true;
  if (authType === 'api-key') {
    const hasKey =
      Boolean(authConfig.apiKey?.trim() || authConfig.key?.trim()) ||
      hasNonEmptyEnvName(authConfig.apiKeyEnv);
    return Boolean(authConfig.headerName?.trim() && hasKey);
  }
  if (authType === 'bearer') {
    return Boolean(authConfig.token?.trim() || hasNonEmptyEnvName(authConfig.tokenEnv));
  }
  if (authType === 'basic') {
    return Boolean(
      authConfig.username?.trim() &&
        (authConfig.password?.trim() || hasNonEmptyEnvName(authConfig.passwordEnv)),
    );
  }
  if (authType === 'oauth-client-credentials') {
    return Boolean(
      authConfig.clientId?.trim() &&
        (authConfig.clientSecret?.trim() ||
          hasNonEmptyEnvName(authConfig.clientSecretEnv) ||
          hasMaskedAuthSecret(authType, authConfig)),
    );
  }
  return false;
}

function hasUsableAuthConfigForProfile(
  authType: AuthType,
  authConfig: Record<string, string>,
  profile: ReturnType<typeof authProfileKindForCapability>,
): boolean {
  if (profile === 'accounts') {
    return Boolean(
      authConfig.accountsApiKey?.trim() ||
        authConfig.flinksApiKey?.trim() ||
        hasNonEmptyEnvName(authConfig.accountsApiKeyEnv) ||
        hasNonEmptyEnvName(authConfig.flinksApiKeyEnv) ||
        isMaskedSecretValue(authConfig.accountsApiKey) ||
        isMaskedSecretValue(authConfig.flinksApiKey),
    );
  }
  return hasUsableAuthConfig(authType, authConfig);
}

function rowsToHeaders(rows: HeaderRow[]): Record<string, string> {
  const headers: Record<string, string> = {};
  for (const row of rows) {
    if (row.key.trim()) headers[row.key.trim()] = row.value;
  }
  return headers;
}

function buildAllHeaders(
  authType: AuthType,
  authConfig: Record<string, string>,
  rows: HeaderRow[],
): Record<string, string> {
  return { ...buildAuthHeaders(authType, authConfig), ...rowsToHeaders(rows) };
}

function stripManagedOauthHeaders(authType: AuthType, headers: Record<string, string>): Record<string, string> {
  if (authType !== 'oauth-client-credentials') return headers;
  return Object.fromEntries(
    Object.entries(headers).filter(([key]) => key.toLowerCase() !== 'authorization'),
  );
}

function headerExists(headers: Record<string, string>, name: string): boolean {
  const lower = name.toLowerCase();
  return Object.keys(headers).some(key => key.toLowerCase() === lower);
}

function headersWithBodyContentType(
  headers: Record<string, string>,
  showBody: boolean,
  bodyMode: RequestBodyType,
): Record<string, string> {
  if (!showBody || headerExists(headers, 'Content-Type')) return headers;
  return {
    ...headers,
    'Content-Type': bodyMode === 'form-urlencoded'
      ? 'application/x-www-form-urlencoded'
      : 'application/json',
  };
}

function parseJson(text: string): { ok: true; value: unknown } | { ok: false; error: string } {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Invalid JSON' };
  }
}

function pathToSegments(path: string): Array<string | number> | null {
  const trimmed = path.trim();
  if (!trimmed) return null;
  const segments: Array<string | number> = [];
  const re = /([^.[\]]+)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;
  let cursor = 0;

  while ((match = re.exec(trimmed)) !== null) {
    if (match.index !== cursor && trimmed[cursor] !== '.') return null;
    segments.push(match[1] ?? Number(match[2]));
    cursor = match.index + match[0].length;
    if (trimmed[cursor] === '.') cursor += 1;
  }

  return cursor === trimmed.length && segments.length > 0 ? segments : null;
}

function parseBuilderValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  try {
    return JSON.parse(trimmed);
  } catch {
    return raw;
  }
}

function setNestedValue(target: Record<string, unknown>, segments: Array<string | number>, value: unknown): void {
  let cursor: any = target;
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const isLast = i === segments.length - 1;
    if (isLast) {
      cursor[segment] = value;
      return;
    }
    const nextSegment = segments[i + 1];
    if (cursor[segment] === undefined || cursor[segment] === null || typeof cursor[segment] !== 'object') {
      cursor[segment] = typeof nextSegment === 'number' ? [] : {};
    }
    cursor = cursor[segment];
  }
}

function buildJsonFromRows(rows: BodyBuilderRow[]): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    if (!row.path.trim()) continue;
    const segments = pathToSegments(row.path);
    if (!segments || typeof segments[0] === 'number') {
      return { ok: false, error: `Invalid JSON path: ${row.path}` };
    }
    setNestedValue(out, segments, parseBuilderValue(row.value));
  }
  return { ok: true, value: out };
}

function setNestedRequestExpression(
  target: Record<string, unknown>,
  segments: string[],
  expression: string,
): void {
  let cursor: Record<string, unknown> = target;
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];
    const isLast = i === segments.length - 1;
    if (isLast) {
      cursor[segment] = expression;
      return;
    }
    const current = cursor[segment];
    if (!current || typeof current !== 'object' || Array.isArray(current)) {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  }
}

function renderJsonataObject(node: Record<string, unknown>, depth = 1): string {
  const entries = Object.entries(node);
  if (entries.length === 0) return '{}';
  const indent = '  '.repeat(depth);
  const closingIndent = '  '.repeat(depth - 1);
  const lines = entries.map(([key, value], index) => {
    const suffix = index === entries.length - 1 ? '' : ',';
    const renderedKey = JSON.stringify(key);
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return `${indent}${renderedKey}: ${renderJsonataObject(value as Record<string, unknown>, depth + 1)}${suffix}`;
    }
    return `${indent}${renderedKey}: ${String(value)}${suffix}`;
  });
  return `{\n${lines.join('\n')}\n${closingIndent}}`;
}

function buildRequestMappingFromRows(
  rows: RequestMappingBuilderRow[],
): { ok: true; value: string } | { ok: false; error: string } {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    const path = row.path.trim();
    const expression = row.expression.trim();
    if (!path && !expression) continue;
    if (!path || !expression) {
      return { ok: false, error: 'Each runtime mapping row needs both a vendor JSON path and an adapter payload expression.' };
    }
    const segments = pathToSegments(path);
    if (!segments || segments.some(segment => typeof segment === 'number')) {
      return { ok: false, error: `Guided runtime mapping supports object paths only. Use Advanced JSONata for arrays: ${path}` };
    }
    setNestedRequestExpression(out, segments.map(segment => String(segment)), expression);
  }
  return { ok: true, value: renderJsonataObject(out) };
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function buildFormObject(
  rows: HeaderRow[],
  authType: AuthType,
  authConfig: Record<string, string>,
  includeSecrets: boolean,
): Record<string, string> {
  const form = rowsToHeaders(rows);
  if (authType === 'oauth-client-credentials') {
    if (!form.grant_type) form.grant_type = 'client_credentials';
    if (!form.client_id) form.client_id = authConfig.clientId ?? '';
    if (!form.scope) form.scope = authConfig.scope ?? MITEK_DEFAULT_SCOPE;
    if (!form.client_secret) {
      form.client_secret = includeSecrets
        ? secretAuthValue(authConfig, 'clientSecret')
        : '{{auth.clientSecret}}';
    }
  }
  return form;
}

function formObjectToBody(form: Record<string, string>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(form)) {
    if (key.trim()) params.set(key.trim(), value);
  }
  return params.toString();
}

function maskFormObject(form: Record<string, string>): Record<string, string> {
  const masked = { ...form };
  if (masked.client_secret) masked.client_secret = SECRET_DISPLAY_VALUE;
  return masked;
}

function flattenPaths(obj: unknown, prefix = ''): string[] {
  if (Array.isArray(obj)) {
    if (obj.length === 0) return prefix ? [`${prefix}[]`] : [];
    return flattenPaths(obj[0], `${prefix}[]`);
  }
  if (obj === null || typeof obj !== 'object') return prefix ? [prefix] : [];

  const paths: string[] = [];
  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val !== null && typeof val === 'object') {
      const nested = flattenPaths(val, path);
      paths.push(...(nested.length > 0 ? nested : [path]));
    } else {
      paths.push(path);
    }
  }
  return paths;
}

function isScalarLike(value: unknown): boolean {
  return value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean';
}

function isScalarArray(value: unknown): boolean {
  return Array.isArray(value) && value.every(item => isScalarLike(item));
}

function isLikelyDynamicRecordPath(path: string): boolean {
  const lastSegment = path.split('.').pop() ?? path;
  return /(responses|answers)$/i.test(lastSegment);
}

function isLikelyDynamicRecordObject(path: string, value: unknown): boolean {
  if (!path || !isRecordLike(value)) return false;
  const entries = Object.entries(value);
  if (entries.length === 0) return false;
  const valuesLookLikeRecordLeaves = entries.every(([, child]) => isScalarLike(child) || isScalarArray(child));
  if (!valuesLookLikeRecordLeaves) return false;

  const hasSampleKey = entries.some(([key]) => /[^A-Za-z0-9_$]/.test(key) || key.length > 32);
  return hasSampleKey || isLikelyDynamicRecordPath(path);
}

function flattenRequestMappingTargetPaths(obj: unknown, prefix = ''): string[] {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return prefix ? [prefix] : [];
  if (isLikelyDynamicRecordObject(prefix, obj)) return [prefix];

  const paths: string[] = [];
  for (const [key, val] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      const nested = flattenRequestMappingTargetPaths(val, path);
      paths.push(...(nested.length > 0 ? nested : [path]));
    } else {
      paths.push(path);
    }
  }
  return paths;
}

function jsonValuePreview(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === 'object') return '{...}';
  if (typeof value === 'string') return `"${value.length > 42 ? `${value.slice(0, 42)}...` : value}"`;
  return String(value);
}

function JsonDraggableTree({
  value,
  path = '',
  label = 'response',
  depth = 0,
  activePath,
  onDragPath,
}: {
  value: unknown;
  path?: string;
  label?: string;
  depth?: number;
  activePath: string | null;
  onDragPath: (path: string) => void;
}) {
  const isContainer = value !== null && typeof value === 'object';
  const dragPath = dragPathForTreeRow(path);
  const rowPath = dragPath;
  const draggable = isContainer || path.length > 0;

  const row = (
    <Box
      draggable={draggable}
      onDragStart={event => {
        if (!draggable) return;
        onDragPath(dragPath);
        event.dataTransfer.setData('text/plain', dragPath);
        event.dataTransfer.effectAllowed = 'copy';
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: RESPONSE_TREE_GRID_COLUMNS,
        gap: 1,
        alignItems: 'center',
        py: 0.45,
        px: 1,
        pl: 1 + depth * 2,
        borderRadius: 0.75,
        cursor: draggable ? 'grab' : 'default',
        bgcolor: activePath === dragPath ? 'action.selected' : 'transparent',
        '&:hover': draggable ? { bgcolor: 'action.hover' } : undefined,
      }}
      title={draggable ? `Drag ${dragPath}` : undefined}
    >
      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: draggable ? 700 : 600, minWidth: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{
        fontFamily: 'monospace',
        minWidth: 0,
        whiteSpace: 'nowrap',
      }}>
        {jsonValuePreview(value)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{
        fontFamily: 'monospace',
        minWidth: 0,
        whiteSpace: 'nowrap',
      }}>
        {rowPath}
      </Typography>
    </Box>
  );

  if (Array.isArray(value)) {
    const first = value[0];
    return (
      <Box>
        {row}
        {first !== undefined && (
          <JsonDraggableTree
            value={first}
            path={path ? `${path}[]` : '[]'}
            label="[0]"
            depth={depth + 1}
            activePath={activePath}
            onDragPath={onDragPath}
          />
        )}
      </Box>
    );
  }

  if (isContainer) {
    return (
      <Box>
        {row}
        {Object.entries(value as Record<string, unknown>).map(([key, child]) => (
          <JsonDraggableTree
            key={`${path}.${key}`}
            value={child}
            path={path ? `${path}.${key}` : key}
            label={key}
            depth={depth + 1}
            activePath={activePath}
            onDragPath={onDragPath}
          />
        ))}
      </Box>
    );
  }

  return row;
}

function headersToRows(headers: Record<string, string> | undefined): HeaderRow[] {
  return Object.entries(headers ?? {}).map(([key, value]) => ({ rowId: uid(), key, value }));
}

function formRowsFromRequestBody(body: unknown): HeaderRow[] {
  if (typeof body === 'string') {
    return Array.from(new URLSearchParams(body).entries())
      .filter(([key]) => key !== 'client_secret')
      .map(([key, value]) => ({ rowId: uid(), key, value }));
  }
  if (body !== null && typeof body === 'object' && !Array.isArray(body)) {
    return Object.entries(body as Record<string, unknown>)
      .filter(([key]) => key !== 'client_secret')
      .map(([key, value]) => ({ rowId: uid(), key, value: typeof value === 'string' ? value : String(value ?? '') }));
  }
  return [];
}

function jsonataExpressionForPayloadPath(path: string): string {
  const expression = jsonataExpressionForPath(path);
  if (!expression) return payloadPathToJsonata(path);
  if (expression === '$') return 'p';
  return expression.startsWith('$.') ? `p.${expression.slice(2)}` : payloadPathToJsonata(path);
}

function canonicalFieldMappingStatusLabel(
  currentPath: string,
  isRequired: boolean,
): string {
  if (currentPath.trim()) return 'Mapped';
  if (isRequired) return 'Drop here';
  return 'Unmapped';
}

function evaluateJsonataWithTimeout(
  expression: string,
  input: unknown,
  timeoutMs = 5000,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      callback();
    };
    const timeoutId = setTimeout(() => {
      finish(() => reject(new Error('Runtime request mapping preview timed out. Check the JSONata expression or sample payload.')));
    }, timeoutMs);

    try {
      jsonata(expression).evaluate(input, undefined, (err, result) => {
        if (err) {
          finish(() => reject(err));
          return;
        }
        finish(() => resolve(result));
      });
    } catch (err) {
      finish(() => reject(err));
    }
  });
}

function pathFromJsonataExpression(expression: string): string {
  const trimmed = expression.trim();
  if (!trimmed) return '';
  if (trimmed === '$') return '$';
  return trimmed.startsWith('$.') ? trimmed.slice(2).replace(/\."([^"]+)"/g, '.$1') : trimmed;
}

function responseMappingToPaths(
  responseMapping: Record<string, string> | undefined,
  fieldMappings: FieldMapping[] | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, expression] of Object.entries(responseMapping ?? {})) {
    out[field] = pathFromJsonataExpression(expression);
  }
  for (const mapping of fieldMappings ?? []) {
    if (!out[mapping.canonicalPath] && mapping.vendorPath) {
      out[mapping.canonicalPath] = mapping.vendorPath;
    }
  }
  return out;
}

interface VendorModalProps {
  open: boolean;
  onClose: () => void;
  existing: VendorMapping | null;
  mode?: 'vendor' | 'add-api' | 'edit-api';
  endpoint?: VendorEndpointMapping | null;
  /** When creating a vendor from the provider list, pre-select this adapter config label. */
  initialConfigLabel?: string;
  /** Load a specific in-progress draft (from list Resume). Omit for fresh onboarding. */
  resumeDraftKey?: string | null;
}

function modalTitle(mode: VendorModalProps['mode'], existing: VendorMapping | null): string {
  if (!existing) return 'Onboard Vendor';
  if (mode === 'add-api') return `Add API for ${vendorDisplayName(existing)}`;
  if (mode === 'edit-api') return `Edit API for ${vendorDisplayName(existing)}`;
  return `Edit Vendor ${vendorDisplayName(existing)}`;
}

export default function VendorModal({
  open,
  onClose,
  existing,
  mode = 'vendor',
  endpoint = null,
  initialConfigLabel,
  resumeDraftKey = null,
}: VendorModalProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isEdit = Boolean(existing);
  const [wizardMode, setWizardMode] = useState<VendorOnboardingMode>(mode);

  const [vendorJsonExportWarning, setVendorJsonExportWarning] = useState<string | null>(null);
  const onVendorJsonExportFailed = useCallback((message: string) => {
    setVendorJsonExportWarning(message);
  }, []);

  const { data: adapterCatalogItems } = useAdapterServiceCatalog({ publishedOnly: true });
  const mergedCapabilityCatalog = useMemo(
    () => mergeAdapterCatalog(LEGACY_CAPABILITY_CATALOG, adapterCatalogItems ?? []),
    [adapterCatalogItems],
  );
  const [selectedConfigLabel, setSelectedConfigLabel] = useState('');
  const catalogGroupsForLabel = useMemo(
    () =>
      selectedConfigLabel.trim()
        ? buildCatalogGroupsForLabel(adapterCatalogItems ?? [], selectedConfigLabel)
        : [],
    [adapterCatalogItems, selectedConfigLabel],
  );
  const useCatalogServicePicker = catalogGroupsForLabel.length > 0;

  const initSessionRef = useRef<string | null>(null);
  const draftHydratedRef = useRef(false);
  const catalogReconciledVendorRef = useRef<string | null>(null);
  const skipDraftPersistUntilRef = useRef(0);
  const providerVariablesTouchedRef = useRef(false);
  const prevOpenRef = useRef(open);
  const skipPersistOnCloseRef = useRef(false);
  const activeDraftContextRef = useRef<{
    mode: VendorOnboardingMode;
    vendorId: string | null;
    endpointCapability: string | null;
  } | null>(null);
  const [resumedFromDraft, setResumedFromDraft] = useState(false);

  const [step, setStep] = useState(0);
  const [requestConfirmOpen, setRequestConfirmOpen] = useState(false);
  const [vendorName, setVendorName] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [serviceName, setServiceName] = useState<RoutingServiceKey>('income-verification');
  const [selectedCatalogGroupKey, setSelectedCatalogGroupKey] = useState<string | null>(null);
  const [capability, setCapability] = useState(
    LEGACY_CAPABILITY_CATALOG.find(c => c.serviceName === 'income-verification')?.capability ?? 'INCOME.BANK_CONNECT',
  );
  const [authType, setAuthType] = useState<AuthType>('none');
  const [authConfig, setAuthConfig] = useState<Record<string, string>>({});
  const [providerVariableRows, setProviderVariableRows] = useState<ProviderVariableRow[]>([]);
  const [providerVariableKeyError, setProviderVariableKeyError] = useState<string | null>(null);
  const [adapterSyncSuccessMessage, setAdapterSyncSuccessMessage] = useState<string | null>(null);
  const [connResult, setConnResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [requestMethod, setRequestMethod] = useState('POST');
  const [requestPath, setRequestPath] = useState('');
  const [endpointTimeoutMs, setEndpointTimeoutMs] = useState<number | ''>(30000);
  const [endpointMaxRetries, setEndpointMaxRetries] = useState<number | ''>(1);
  const [endpointRetryDelayMs, setEndpointRetryDelayMs] = useState<number | ''>(500);
  const [endpointBodyFormat, setEndpointBodyFormat] = useState<RequestBodyType | ''>('');
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([]);
  const [showBody, setShowBody] = useState(false);
  const [bodyMode, setBodyMode] = useState<RequestBodyType>('json');
  const [bodyText, setBodyText] = useState('{\n  \n}');
  const [bodyBuilderRows, setBodyBuilderRows] = useState<BodyBuilderRow[]>([]);
  const [formBodyRows, setFormBodyRows] = useState<HeaderRow[]>([]);
  const [bodyBuilderError, setBodyBuilderError] = useState<string | null>(null);
  const [requestMappingRows, setRequestMappingRows] = useState<RequestMappingBuilderRow[]>([]);
  const [requestMappingText, setRequestMappingText] = useState('');
  const [requestMappingSampleText, setRequestMappingSampleText] = useState('{\n  "requestId": "preview-request-id"\n}');
  const [requestMappingBuilderActive, setRequestMappingBuilderActive] = useState(false);
  const [requestMappingViewMode, setRequestMappingViewMode] = useState<'fields' | 'jsonata'>('fields');
  const [requestMappingBuilderError, setRequestMappingBuilderError] = useState<string | null>(null);
  const [requestMappingValidation, setRequestMappingValidation] = useState<RequestMappingValidation>({ ok: true });
  const [requestMappingEvaluationNonce, setRequestMappingEvaluationNonce] = useState(0);
  const [liveResult, setLiveResult] = useState<LiveTestResult | null>(null);
  const [mappingPaths, setMappingPaths] = useState<Record<string, string>>({});
  const [draggingPath, setDraggingPath] = useState<string | null>(null);
  const [savedEndpointMappings, setSavedEndpointMappings] = useState<VendorEndpointMapping[]>([]);
  const [showCanonicalRequest, setShowCanonicalRequest] = useState(false);
  const [showCanonicalResponse, setShowCanonicalResponse] = useState(false);

  const selectedCatalogGroup = useMemo(
    () => catalogGroupsForLabel.find(g => g.key === selectedCatalogGroupKey) ?? null,
    [catalogGroupsForLabel, selectedCatalogGroupKey],
  );
  const adapterServiceKeyForPreset = selectedCatalogGroup?.adapterServiceKey?.trim() ?? '';

  const selectedCapability = useMemo(
    () => capabilityForSelection(mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey, capability),
    [mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey, capability],
  );
  const selectedStepKey = useMemo(
    () => resolveStepKey({
      stepKey: selectedCapability.stepKey,
      adapterServiceKey: adapterServiceKeyForPreset || adapterEndpointServiceName(selectedCapability),
      capability: selectedCapability.capability,
    }),
    [selectedCapability, adapterServiceKeyForPreset],
  );
  const authProfileKind = useMemo(
    () => authProfileKindForCapability({
      stepKey: selectedStepKey,
      adapterServiceKey: adapterServiceKeyForPreset || adapterEndpointServiceName(selectedCapability),
      capability: selectedCapability.capability,
    }),
    [selectedStepKey, adapterServiceKeyForPreset, selectedCapability],
  );
  const isCreateVendorOnly = !existing && wizardMode === 'vendor';
  const showVendorShellConnect = wizardMode === 'vendor';
  const showPerApiAuthOnConnect = wizardMode === 'add-api' || wizardMode === 'edit-api';
  const catalogContextBanner = useMemo(
    () => catalogContextLabel(selectedCapability),
    [selectedCapability],
  );
  const adapterCatalogMissingFields =
    isAdapterBackedCapability(selectedCapability.capability) &&
    runtimeRequestFieldsFromTargets(selectedCapability.requestMappingTargets).length === 0 &&
    selectedCapability.mappingTargets.length === 0;
  /** Copy per capability so React remaps rows when schema or capability changes (avoids stale TextField state). */
  const selectedFields = useMemo(
    () => [...selectedCapability.mappingTargets],
    [selectedCapability],
  );
  const runtimeMappingTemplate = useMemo(
    () => defaultRuntimeMappingTemplateForStepKey(selectedStepKey),
    [selectedStepKey],
  );
  const runtimeRequestFields = useMemo(
    () => runtimeRequestFieldsForCapability(selectedCapability),
    [selectedCapability],
  );
  const selectableCapabilityOptions = useMemo(
    () => selectableCapabilities(mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey),
    [mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey],
  );
  const canAddAnotherApi = useMemo(
    () => canAddApiForVendorGroup(
      mergedCapabilityCatalog,
      serviceName,
      selectedCatalogGroupKey,
      savedEndpointMappings,
    ),
    [mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey, savedEndpointMappings],
  );
  const addApiSlotCounts = useMemo(() => {
    const pool = selectableCapabilitiesForGroup(mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey);
    const remaining = unclaimedCapabilitiesForGroup(
      mergedCapabilityCatalog,
      serviceName,
      selectedCatalogGroupKey,
      savedEndpointMappings,
    );
    return {
      configured: pool.length - remaining.length,
      total: pool.length,
    };
  }, [mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey, savedEndpointMappings]);
  const addApiDisabledMessage = vendorAddApiDisabledMessage(addApiSlotCounts);
  const addApiCatalogFull = wizardMode === 'add-api' && !canAddAnotherApi;
  const requestMappingCanContinue =
    !requestMappingBuilderError &&
    (!requestMappingText.trim() || (requestMappingValidation.ok && !requestMappingValidation.pending));
  const selectedBody = useMemo(() => {
    if (!showBody) return { ok: true, value: undefined } as const;
    if (bodyMode === 'form-urlencoded') {
      return { ok: true, value: formObjectToBody(buildFormObject(formBodyRows, authType, authConfig, true)) } as const;
    }
    return parseJson(bodyText);
  }, [authConfig, authType, bodyMode, bodyText, formBodyRows, showBody]);

  const bodyPreview = useMemo(() => {
    if (!showBody) return undefined;
    if (bodyMode === 'form-urlencoded') {
      return maskFormObject(buildFormObject(formBodyRows, authType, authConfig, false));
    }
    return selectedBody.ok ? selectedBody.value : undefined;
  }, [authConfig, authType, bodyMode, formBodyRows, selectedBody, showBody]);

  const requestMappingSampleJson = useMemo(
    () => parseJson(requestMappingSampleText || '{}'),
    [requestMappingSampleText],
  );

  const requestMappingTargetPaths = useMemo(() => {
    if (!showBody) return [];
    if (bodyMode === 'form-urlencoded') {
      return Array.from(new Set(formBodyRows.map(row => row.key.trim()).filter(Boolean))).sort();
    }
    if (!selectedBody.ok || !isRecordLike(selectedBody.value)) return [];
    return Array.from(new Set(flattenRequestMappingTargetPaths(selectedBody.value))).sort();
  }, [bodyMode, formBodyRows, selectedBody, showBody]);

  const requestMappingDisplayRows = useMemo<RequestMappingDisplayRow[]>(() => {
    const rowsByPath = new Map(
      requestMappingRows
        .map(row => [row.path.trim(), row] as const)
        .filter(([path]) => path.length > 0),
    );
    const targetPathSet = new Set(requestMappingTargetPaths);
    const generatedRows = requestMappingTargetPaths.map(path => {
      const mapped = rowsByPath.get(path);
      return {
        rowId: mapped?.rowId ?? `body:${path}`,
        path,
        expression: mapped?.expression ?? '',
        fromBody: true,
        mapped: Boolean(mapped?.expression.trim()),
      };
    });
    const manualRows = requestMappingRows
      .filter(row => !row.path.trim() || !targetPathSet.has(row.path.trim()))
      .map(row => ({
        ...row,
        fromBody: false,
        mapped: Boolean(row.expression.trim()),
      }));
    return [...generatedRows, ...manualRows];
  }, [requestMappingRows, requestMappingTargetPaths]);

  const providerVariables = useMemo(
    () => providerVariablesFromRows(providerVariableRows),
    [providerVariableRows],
  );

  const flatPaths = useMemo(
    () => liveResult?.responseBody !== null && typeof liveResult?.responseBody === 'object'
      ? Array.from(new Set(flattenPaths(liveResult.responseBody))).sort()
      : [],
    [liveResult?.responseBody],
  );

  useEffect(() => {
    const mapping = requestMappingText.trim();
    if (!mapping) {
      setRequestMappingValidation({ ok: true, preview: undefined });
      return;
    }

    const parsedSample = parseJson(requestMappingSampleText || '{}');
    if (!parsedSample.ok) {
      setRequestMappingValidation({
        ok: false,
        error: `Sample adapter payload must be valid JSON: ${parsedSample.error}`,
      });
      return;
    }

    let cancelled = false;
    setRequestMappingValidation({ ok: false, pending: true });

    const evaluate = async () => {
      try {
        const payload = parsedSample.value;
        const p = isRecordLike(payload) ? payload : {};
        const evalContext = {
          p,
          payload: p,
          c: buildPreviewProviderContext(vendorId.trim() || existing?.vendorId || 'preview-vendor', providerVariables),
          e: {},
          r: {
            requestId: 'preview-request-id',
            serviceName,
            step: selectedCapability.capability,
            timestamp: new Date(0).toISOString(),
          },
        };
        const result = await evaluateJsonataWithTimeout(mapping, evalContext);
        if (cancelled) return;
        if (!isRecordLike(result)) {
          setRequestMappingValidation({
            ok: false,
            error: 'Runtime request mapping must evaluate to a JSON object.',
            preview: result,
          });
          return;
        }
        setRequestMappingValidation({ ok: true, preview: result });
      } catch (err) {
        if (cancelled) return;
        setRequestMappingValidation({
          ok: false,
          error: formatJsonataError(err),
        });
      }
    };

    void evaluate();

    return () => {
      cancelled = true;
    };
  }, [
    requestMappingEvaluationNonce,
    requestMappingSampleText,
    requestMappingText,
    selectedCapability.capability,
    serviceName,
    vendorId,
    existing?.vendorId,
    providerVariables,
  ]);

  useEffect(() => {
    if (!requestMappingBuilderActive) return;
    const hasStartedRow = requestMappingRows.some(row => row.path.trim() || row.expression.trim());
    if (!hasStartedRow) {
      setRequestMappingBuilderError(null);
      setRequestMappingText('');
      setRequestMappingValidation({ ok: true });
      return;
    }
    const built = buildRequestMappingFromRows(requestMappingRows);
    if (!built.ok) {
      setRequestMappingBuilderError(built.error);
      return;
    }
    setRequestMappingBuilderError(null);
    setRequestMappingText(built.value);
    setRequestMappingValidation({ ok: false, pending: true });
    setRequestMappingEvaluationNonce(prev => prev + 1);
  }, [requestMappingBuilderActive, requestMappingRows]);

  const setAuth = useCallback((field: string, value: string) => {
    setAuthConfig(prev => ({ ...prev, [field]: value }));
    setConnResult(null);
  }, []);

  const resolveLabelForVendor = useCallback(
    (vendor: VendorMapping | null | undefined, fallbackLabel?: string) => {
      const resolved = vendor
        ? resolveVendorAdapterConfigLabel(
            {
              adapterConfigLabel: vendor.adapterConfigLabel,
              capability: vendor.capability,
              endpointMappings: vendor.endpointMappings,
            },
            adapterCatalogItems ?? [],
          )
        : undefined;
      if (resolved) return resolved;
      if (fallbackLabel?.trim()) return normalizeConfigLabel(fallbackLabel);
      if (initialConfigLabel?.trim()) return normalizeConfigLabel(initialConfigLabel);
      return '';
    },
    [adapterCatalogItems, initialConfigLabel],
  );

  const loadEndpointForEditing = useCallback((target: VendorEndpointMapping, fallbackService: RoutingServiceKey) => {
    const nextService = normalizeRoutingSlug(target.serviceName) ?? fallbackService;
    const resolvedLabel = resolveLabelForVendor(existing);
    setSelectedConfigLabel(resolvedLabel);
    const capMeta = findCapabilityOptionByLabel(
      mergedCapabilityCatalog,
      target.capability,
      resolvedLabel,
    );
    const groupForCap = capMeta?.catalogGroupKey ?? null;
    if (groupForCap) setSelectedCatalogGroupKey(groupForCap);
    const nextCapability = capabilityForSelection(mergedCapabilityCatalog, nextService, groupForCap, target.capability);
    setServiceName(nextService);
    setCapability(nextCapability.capability);
    setRequestMethod(target.requestMethod ?? 'POST');
    setRequestPath(target.requestPath ?? '');
    setEndpointTimeoutMs(target.timeoutMs ?? 30000);
    setEndpointMaxRetries(target.maxRetries ?? 1);
    setEndpointRetryDelayMs(target.retryDelayMs ?? 500);
    setEndpointBodyFormat(target.bodyFormat ?? target.requestBodyType ?? '');
    setHeaderRows(headersToRows(target.requestHeaders));
    setShowBody(target.requestBody !== undefined && target.requestBody !== null);
    setBodyMode(target.requestBodyType ?? 'json');
    setBodyText((target.requestBodyType ?? 'json') === 'json' && target.requestBody !== undefined && target.requestBody !== null
      ? JSON.stringify(target.requestBody, null, 2)
      : '{\n  \n}');
    setBodyBuilderRows([]);
    setFormBodyRows((target.requestBodyType ?? 'json') === 'form-urlencoded'
      ? formRowsFromRequestBody(target.requestBody)
      : []);
    setRequestMappingRows([]);
    setRequestMappingBuilderActive(false);
    setRequestMappingText(target.requestMapping ?? '');
    setRequestMappingSampleText(defaultRuntimeSamplePayloadText(nextCapability, providerVariables));
    setRequestMappingBuilderError(null);
    setLiveResult(target.sampleResponse !== undefined && target.sampleResponse !== null
      ? {
          statusCode: 200,
          statusText: 'Saved Sample',
          ok: true,
          durationMs: 0,
          responseBody: target.sampleResponse,
        }
      : null);
    setMappingPaths(responseMappingToPaths(target.responseMapping, target.fieldMappings));
    const vendorAuth = existing?.authConfig ?? {};
    const mergedAuth = mergeEffectiveAuthConfig(vendorAuth, target.authConfig);
    const profile = authProfileKindForCapability({
      stepKey: nextCapability.stepKey,
      adapterServiceKey: adapterEndpointServiceName(nextCapability),
      capability: nextCapability.capability,
    });
    setAuthConfig(mergedAuth);
    setAuthType(inferAuthTypeFromAuthConfig(mergedAuth, profile));
  }, [existing, mergedCapabilityCatalog, providerVariables, existing?.authConfig, resolveLabelForVendor]);

  const resetBlankCapabilityForm = useCallback((cap: CapabilityOption) => {
    setCapability(cap.capability);
    setRequestMethod('POST');
    setRequestPath(cap.endpointTemplate ?? '');
    setEndpointTimeoutMs(30000);
    setEndpointMaxRetries(1);
    setEndpointRetryDelayMs(500);
    setEndpointBodyFormat('');
    setHeaderRows([]);
    setShowBody(false);
    setBodyMode('json');
    setBodyText('{\n  \n}');
    setBodyBuilderRows([]);
    setFormBodyRows([]);
    setRequestMappingRows([]);
    setRequestMappingBuilderActive(false);
    setRequestMappingText('');
    setRequestMappingSampleText(defaultRuntimeSamplePayloadText(cap, providerVariables));
    setRequestMappingBuilderError(null);
    setMappingPaths({});
    setLiveResult(null);
    setConnResult(null);
    setAuthType('none');
    setAuthConfig({});
    const sample = samplePayloadForCapability(cap);
    if (sample && typeof sample === 'object' && Object.keys(sample as object).length > 0) {
      setShowBody(true);
      setBodyMode('json');
      setBodyText(JSON.stringify(sample, null, 2));
    }
  }, [providerVariables]);

  const onboardingMode = open ? mode : wizardMode;

  useEffect(() => {
    if (open) setWizardMode(mode);
  }, [open, mode]);

  if (open) {
    activeDraftContextRef.current = {
      mode,
      vendorId: existing?.vendorId ?? null,
      endpointCapability: endpoint?.capability ?? null,
    };
  }

  const draftContextVendorId = existing?.vendorId ?? null;
  const effectiveVendorIdForDraft = effectiveDraftVendorId(draftContextVendorId, vendorId);

  const buildSessionKey = useCallback(
    () => `${onboardingMode}:${existing?.vendorId ?? 'new'}:${endpoint?.capability ?? 'none'}`,
    [onboardingMode, existing?.vendorId, endpoint?.capability],
  );

  const buildDraftSnapshot = useCallback((): VendorOnboardingDraftSnapshot => ({
    step,
    selectedConfigLabel,
    vendorName,
    vendorId,
    baseUrl,
    serviceName,
    selectedCatalogGroupKey,
    capability,
    authType,
    authConfig,
    providerVariables: samplePayloadVars(providerVariableRows),
    requestMethod,
    requestPath,
    endpointTimeoutMs,
    endpointMaxRetries,
    endpointRetryDelayMs,
    endpointBodyFormat,
    headerRows,
    showBody,
    bodyMode,
    bodyText,
    bodyBuilderRows,
    formBodyRows,
    requestMappingRows,
    requestMappingText,
    requestMappingSampleText,
    requestMappingBuilderActive,
    mappingPaths,
    savedEndpointMappings,
    liveResult,
    showCanonicalRequest,
    showCanonicalResponse,
  }), [
    step,
    selectedConfigLabel,
    vendorName,
    vendorId,
    baseUrl,
    serviceName,
    selectedCatalogGroupKey,
    capability,
    authType,
    authConfig,
    providerVariableRows,
    requestMethod,
    requestPath,
    endpointTimeoutMs,
    endpointMaxRetries,
    endpointRetryDelayMs,
    endpointBodyFormat,
    headerRows,
    showBody,
    bodyMode,
    bodyText,
    bodyBuilderRows,
    formBodyRows,
    requestMappingRows,
    requestMappingText,
    requestMappingSampleText,
    requestMappingBuilderActive,
    mappingPaths,
    savedEndpointMappings,
    liveResult,
    showCanonicalRequest,
    showCanonicalResponse,
  ]);

  const persistCurrentDraft = useCallback(() => {
    if (Date.now() < skipDraftPersistUntilRef.current) return;
    const ctx = activeDraftContextRef.current;
    if (!ctx || ctx.mode === 'edit-api' || ctx.mode === 'add-api') return;

    const snapshot = buildDraftSnapshot();
    if (!hasMeaningfulProgress(snapshot)) return;

    const draftVendorId =
      ctx.vendorId ?? (ctx.mode === 'vendor' ? null : effectiveDraftVendorId(null, snapshot.vendorId));
    const draftKey = buildDraftKey({
      mode: ctx.mode,
      vendorId: draftVendorId,
      endpointCapability: ctx.endpointCapability,
      configLabel: selectedConfigLabel,
    });
    saveVendorOnboardingDraft(draftKey, {
      mode: ctx.mode,
      vendorId: draftVendorId,
      endpointCapability: ctx.endpointCapability,
      configLabel: selectedConfigLabel,
      vendorName: snapshot.vendorName,
      step: snapshot.step,
    }, snapshot);
  }, [buildDraftSnapshot, selectedConfigLabel]);

  const clearCurrentDraft = useCallback(() => {
    const ctx = activeDraftContextRef.current;
    if (!ctx) return;

    const draftVendorId = ctx.vendorId ?? (ctx.mode === 'vendor' ? null : effectiveVendorIdForDraft);
    clearVendorOnboardingDraft(buildDraftKey({
      mode: ctx.mode,
      vendorId: draftVendorId,
      endpointCapability: ctx.endpointCapability,
      configLabel: selectedConfigLabel,
    }));
    clearDraftsForContext({
      mode: ctx.mode,
      vendorId: draftVendorId,
      endpointCapability: ctx.endpointCapability,
    });
  }, [effectiveVendorIdForDraft, selectedConfigLabel]);

  const handleCloseAfterSuccess = useCallback(() => {
    skipPersistOnCloseRef.current = true;
    clearCurrentDraft();
    const savedVendorId = existing?.vendorId ?? vendorId.trim();
    if (savedVendorId) clearDraftsForVendorId(savedVendorId);
    initSessionRef.current = null;
    setResumedFromDraft(false);
    setAdapterSyncSuccessMessage(null);
    onClose();
  }, [clearCurrentDraft, existing?.vendorId, onClose, vendorId]);

  const handleApiDraftSaveSuccess = useCallback(() => {
    skipPersistOnCloseRef.current = true;
    initSessionRef.current = null;
    setResumedFromDraft(false);
    onClose();
  }, [onClose]);

  const handleVendorSaveSuccess = useCallback((data: VendorMapping) => {
    const sync = (data as VendorMappingSaveResponse).thirdPartyConfigSync;
    if (sync && sync.ok) {
      setAdapterSyncSuccessMessage(
        `Adapter config synced to label "${sync.label}" (${sync.vendorCount} vendor(s)).`,
      );
      window.setTimeout(() => {
        handleCloseAfterSuccess();
      }, 2000);
      return;
    }
    handleCloseAfterSuccess();
  }, [handleCloseAfterSuccess]);

  const createVendor = useCreateVendor(undefined, handleVendorSaveSuccess, onVendorJsonExportFailed);
  const updateVendor = useUpdateVendor(undefined, handleVendorSaveSuccess, onVendorJsonExportFailed);
  const updateVendorDraft = useUpdateVendor(undefined, undefined, onVendorJsonExportFailed);
  const updateVendorForApiDraft = useUpdateVendor(undefined, handleApiDraftSaveSuccess, onVendorJsonExportFailed);
  const testConn = useTestConnection();
  const liveTest = useLiveTest();
  // Both test endpoints require vendors.live_test; without it the buttons are hidden.
  const canLiveTest = hasAdminPermission(useAdminConsoleMe().data, PermissionKey.VENDORS_LIVE_TEST);

  const resetTransientUiState = useCallback(() => {
    setRequestConfirmOpen(false);
    setConnResult(null);
    setBodyBuilderError(null);
    setRequestMappingBuilderError(null);
    setVendorJsonExportWarning(null);
  }, []);

  const draftSetters = useMemo(() => ({
    setStep,
    setSelectedConfigLabel,
    setVendorName,
    setVendorId,
    setBaseUrl,
    setServiceName,
    setSelectedCatalogGroupKey,
    setCapability,
    setAuthType,
    setAuthConfig,
    setProviderVariables: (value: Record<string, string>) => {
      setProviderVariableRows(providerVariableRowsFromRecord(value));
    },
    setRequestMethod,
    setRequestPath,
    setEndpointTimeoutMs,
    setEndpointMaxRetries,
    setEndpointRetryDelayMs,
    setEndpointBodyFormat,
    setHeaderRows,
    setShowBody,
    setBodyMode,
    setBodyText,
    setBodyBuilderRows,
    setFormBodyRows,
    setRequestMappingRows,
    setRequestMappingText,
    setRequestMappingSampleText,
    setRequestMappingBuilderActive,
    setMappingPaths,
    setSavedEndpointMappings,
    setLiveResult,
    setShowCanonicalRequest,
    setShowCanonicalResponse,
  }), []);

  const initializeFromServer = useCallback((options?: { discardFresh?: boolean }) => {
    setResumedFromDraft(false);
    resetTransientUiState();
    if (options?.discardFresh) {
      providerVariablesTouchedRef.current = false;
    }

    if (existing) {
      const hydrationLabel = resolveLabelForVendor(existing, initialConfigLabel);
      setSelectedConfigLabel(hydrationLabel);
      const vendorContext = {
        preferredCatalogGroupKey: existing.preferredCatalogGroupKey,
        endpointMappings: existing.endpointMappings,
        serviceName: existing.serviceName,
        capability: existing.capability,
      };
      const { groupKey } = resolveVendorCatalogGroup(
        vendorContext,
        adapterCatalogItems ?? [],
        hydrationLabel,
      );
      const firstEndpoint = existing.endpointMappings?.[0];
      const loadedService = resolveVendorRoutingSlug(
        vendorContext,
        adapterCatalogItems ?? [],
        mergedCapabilityCatalog,
        endpoint ?? firstEndpoint ?? null,
        hydrationLabel,
      );

      setVendorName(vendorDisplayName(existing));
      setVendorId(existing.vendorId);
      setBaseUrl(existing.baseUrl ?? '');
      setServiceName(loadedService);
      setAuthType(existing.authType ?? 'none');
      setAuthConfig(existing.authConfig ?? {});
      const loadedProviderVars = providerVariableRowsFromRecord(existing.providerVariables);
      setProviderVariableRows(loadedProviderVars);
      providerVariablesTouchedRef.current = loadedProviderVars.length > 0;
      setSavedEndpointMappings(existing.endpointMappings ?? []);
      if (mode === 'edit-api' && endpoint) {
        loadEndpointForEditing(endpoint, loadedService);
        setStep(
          endpoint.onboardingStatus === 'draft'
            ? Math.max(0, Math.min(3, endpoint.onboardingStep ?? 0))
            : 0,
        );
      } else if (mode === 'add-api') {
        if (groupKey) setSelectedCatalogGroupKey(groupKey);
        const nextCap = firstUnclaimedCapability(
          mergedCapabilityCatalog,
          loadedService,
          groupKey,
          existing.endpointMappings ?? [],
        );
        resetBlankCapabilityForm(nextCap);
        setStep(0);
      } else {
        if (groupKey) setSelectedCatalogGroupKey(groupKey);
        setCapability(firstSelectableCapability(mergedCapabilityCatalog, loadedService, groupKey).capability);
        setRequestMethod('POST');
        setRequestPath('');
        setHeaderRows([]);
        setShowBody(false);
        setBodyMode('json');
        setBodyText('{\n  \n}');
        setBodyBuilderRows([]);
        setFormBodyRows([]);
        setRequestMappingRows([]);
        setRequestMappingBuilderActive(false);
        setRequestMappingText('');
        setRequestMappingSampleText(
          defaultRuntimeSamplePayloadText(firstSelectableCapability(mergedCapabilityCatalog, loadedService, groupKey)),
        );
        setRequestMappingBuilderError(null);
        setMappingPaths({});
        setLiveResult(null);
        setStep(0);
      }
      return;
    }

    const createLabel = initialConfigLabel?.trim() ? normalizeConfigLabel(initialConfigLabel) : '';
    const groupsForCreate = createLabel
      ? buildCatalogGroupsForLabel(adapterCatalogItems ?? [], createLabel)
      : [];
    const firstGroup = groupsForCreate[0] ?? null;
    const initialService: RoutingServiceKey =
      firstGroup?.serviceName ??
      normalizeRoutingSlug(mergedCapabilityCatalog[0]?.serviceName) ??
      'income-verification';
    setSelectedConfigLabel(createLabel);
    setVendorName('');
    setVendorId('');
    setBaseUrl('');
    setServiceName(initialService);
    setSelectedCatalogGroupKey(firstGroup?.key ?? null);
    setProviderVariableRows([]);
    if (options?.discardFresh) {
      providerVariablesTouchedRef.current = false;
    }
    setCapability(
      firstSelectableCapability(mergedCapabilityCatalog, initialService, firstGroup?.key ?? null).capability,
    );
    setAuthType('none');
    setAuthConfig({});
    setRequestMethod('POST');
    setRequestPath('');
    setHeaderRows([]);
    setShowBody(false);
    setBodyMode('json');
    setBodyText('{\n  \n}');
    setBodyBuilderRows([]);
    setFormBodyRows([]);
    setRequestMappingRows([]);
    setRequestMappingBuilderActive(false);
    setRequestMappingText('');
    setRequestMappingSampleText(
      defaultRuntimeSamplePayloadText(
        firstSelectableCapability(mergedCapabilityCatalog, initialService, firstGroup?.key ?? null),
        {},
      ),
    );
    setRequestMappingBuilderError(null);
    setMappingPaths({});
    setLiveResult(null);
    setSavedEndpointMappings([]);
    setStep(0);
  }, [
    adapterCatalogItems,
    endpoint,
    existing,
    initialConfigLabel,
    loadEndpointForEditing,
    mergedCapabilityCatalog,
    mode,
    resetBlankCapabilityForm,
    resolveLabelForVendor,
    resetTransientUiState,
  ]);

  const hasSavedDraft = useMemo(
    () => findDraftForContext({
      mode: onboardingMode,
      vendorId: draftContextVendorId,
      endpointCapability: endpoint?.capability ?? null,
      configLabel: selectedConfigLabel,
    }) !== null,
    [onboardingMode, draftContextVendorId, endpoint?.capability, selectedConfigLabel, open, step],
  );

  useEffect(() => {
    if (prevOpenRef.current && !open) {
      const ctx = activeDraftContextRef.current;
      if (ctx?.mode === 'edit-api' || ctx?.mode === 'add-api') {
        clearDraftsForContext({
          mode: ctx.mode,
          vendorId: ctx.vendorId,
          endpointCapability: ctx.endpointCapability,
        });
        clearDraftsForContext({
          mode: ctx.mode,
          vendorId: ctx.vendorId,
          endpointCapability: null,
        });
      } else if (skipPersistOnCloseRef.current) {
        skipPersistOnCloseRef.current = false;
      } else if (ctx) {
        persistCurrentDraft();
      }
      activeDraftContextRef.current = null;
    }
    prevOpenRef.current = open;
  }, [open, persistCurrentDraft]);

  useEffect(() => {
    if (!open) {
      initSessionRef.current = null;
      catalogReconciledVendorRef.current = null;
      draftHydratedRef.current = false;
      setResumedFromDraft(false);
      return;
    }
    if (draftHydratedRef.current) return;

    const sessionKey = buildSessionKey();
    const hydrationLabel = existing
      ? resolveLabelForVendor(existing, initialConfigLabel)
      : initialConfigLabel?.trim()
        ? normalizeConfigLabel(initialConfigLabel)
        : '';

    if (resumeDraftKey) {
      const restoredByKey = loadVendorOnboardingDraft(resumeDraftKey);
      if (restoredByKey) {
        applyVendorModalDraft(restoredByKey.snapshot, draftSetters);
        providerVariablesTouchedRef.current =
          Object.keys(restoredByKey.snapshot.providerVariables ?? {}).length > 0;
        resetTransientUiState();
        setResumedFromDraft(true);
        initSessionRef.current = sessionKey;
        draftHydratedRef.current = true;
        return;
      }
    }

    const restored =
      !existing && mode === 'vendor'
        ? null
        : findDraftForContext({
            mode: onboardingMode,
            vendorId: existing?.vendorId ?? null,
            endpointCapability: endpoint?.capability ?? null,
            configLabel: hydrationLabel,
          });

    if (restored) {
      applyVendorModalDraft(restored.snapshot, draftSetters);
      providerVariablesTouchedRef.current =
        Object.keys(restored.snapshot.providerVariables ?? {}).length > 0;
      resetTransientUiState();
      setResumedFromDraft(true);
      initSessionRef.current = sessionKey;
      draftHydratedRef.current = true;
      return;
    }

    if (initSessionRef.current === sessionKey) {
      resetTransientUiState();
      draftHydratedRef.current = true;
      return;
    }

    initializeFromServer();
    initSessionRef.current = sessionKey;
    draftHydratedRef.current = true;
  }, [
    open,
    onboardingMode,
    mode,
    existing?.vendorId,
    endpoint?.capability,
    existing,
    initialConfigLabel,
    resumeDraftKey,
    resolveLabelForVendor,
    buildSessionKey,
    draftSetters,
    initializeFromServer,
    resetTransientUiState,
  ]);

  useEffect(() => {
    if (!open || !existing?.vendorId || !adapterCatalogItems?.length) return;
    if (catalogReconciledVendorRef.current === existing.vendorId) return;
    if (resumedFromDraft) {
      catalogReconciledVendorRef.current = existing.vendorId;
      return;
    }

    const label = resolveLabelForVendor(existing, initialConfigLabel);
    setSelectedConfigLabel(label);
    const { groupKey, group } = resolveVendorCatalogGroup(
      {
        preferredCatalogGroupKey: existing.preferredCatalogGroupKey,
        endpointMappings: existing.endpointMappings,
        serviceName: existing.serviceName,
        capability: existing.capability,
      },
      adapterCatalogItems,
      label,
    );
    if (groupKey) {
      setSelectedCatalogGroupKey(groupKey);
      if (group) setServiceName(group.serviceName);
    }
    catalogReconciledVendorRef.current = existing.vendorId;
  }, [
    open,
    existing,
    adapterCatalogItems,
    initialConfigLabel,
    resolveLabelForVendor,
    serviceName,
    resumedFromDraft,
  ]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      persistCurrentDraft();
    }, 500);
    return () => window.clearTimeout(timer);
  }, [open, persistCurrentDraft, buildDraftSnapshot]);

  const updateConfigLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) {
      setSelectedConfigLabel('');
      setSelectedCatalogGroupKey(null);
      return;
    }
    const normalized = normalizeConfigLabel(trimmed);
    setSelectedConfigLabel(normalized);
    const groups = buildCatalogGroupsForLabel(adapterCatalogItems ?? [], normalized);
    const firstGroup = groups[0];
    if (firstGroup) {
      setSelectedCatalogGroupKey(firstGroup.key);
      setServiceName(firstGroup.serviceName);
    } else {
      setSelectedCatalogGroupKey(null);
    }
  };

  const markProviderVariablesTouched = useCallback(() => {
    providerVariablesTouchedRef.current = true;
  }, []);

  const updateCatalogGroup = (groupKey: string) => {
    const group = catalogGroupsForLabel.find(g => g.key === groupKey);
    if (!group) return;
    setSelectedCatalogGroupKey(groupKey);
    setServiceName(group.serviceName);
    const nextCap = wizardMode === 'add-api'
      ? firstUnclaimedCapability(mergedCapabilityCatalog, group.serviceName, groupKey, savedEndpointMappings)
      : firstSelectableCapability(mergedCapabilityCatalog, group.serviceName, groupKey);
    if (wizardMode === 'add-api') {
      resetBlankCapabilityForm(nextCap);
      return;
    }
    setCapability(nextCap.capability);
    setRequestMappingSampleText(defaultRuntimeSamplePayloadText(nextCap, providerVariables));
    setRequestMappingRows([]);
    setRequestMappingBuilderActive(false);
    setRequestMappingText('');
    setRequestMappingBuilderError(null);
    setMappingPaths({});
    setLiveResult(null);
  };

  const updateCapability = (nextCapability: string) => {
    const next = capabilityForSelection(mergedCapabilityCatalog, serviceName, selectedCatalogGroupKey, nextCapability);
    const adapterKey = adapterEndpointServiceName(next);

    if (wizardMode === 'add-api') {
      if (isCapabilityClaimed(savedEndpointMappings, nextCapability, serviceName, adapterKey)) {
        return;
      }
      resetBlankCapabilityForm(next);
      return;
    }

    setCapability(nextCapability);
    setMappingPaths({});
    setDraggingPath(null);
    const configuredEndpoint = findSavedEndpointMapping(
      savedEndpointMappings,
      nextCapability,
      serviceName,
      adapterKey,
    );
    if (configuredEndpoint) {
      loadEndpointForEditing(configuredEndpoint, serviceName);
      setLiveResult(configuredEndpoint.sampleResponse !== undefined && configuredEndpoint.sampleResponse !== null
        ? {
            statusCode: 200,
            statusText: 'Saved Sample',
            ok: true,
            durationMs: 0,
            responseBody: configuredEndpoint.sampleResponse,
          }
        : null);
      return;
    }
    resetBlankCapabilityForm(next);
  };

  useEffect(() => {
    if (!isEdit) {
      setVendorId(slugVendorName(vendorName));
    }
  }, [isEdit, vendorName]);

  const addHeaderRow = () => {
    setHeaderRows(prev => [...prev, { rowId: uid(), key: '', value: '' }]);
  };

  const updateHeaderRow = (rowId: string, field: 'key' | 'value', value: string) => {
    setHeaderRows(prev => prev.map(row => row.rowId === rowId ? { ...row, [field]: value } : row));
    setLiveResult(null);
  };

  const removeHeaderRow = (rowId: string) => {
    setHeaderRows(prev => prev.filter(row => row.rowId !== rowId));
    setLiveResult(null);
  };

  const addBodyBuilderRow = () => {
    setBodyBuilderRows(prev => [...prev, { rowId: uid(), path: '', value: '' }]);
    setBodyBuilderError(null);
  };

  const updateBodyBuilderRow = (rowId: string, field: 'path' | 'value', value: string) => {
    setBodyBuilderRows(prev => prev.map(row => row.rowId === rowId ? { ...row, [field]: value } : row));
    setBodyBuilderError(null);
  };

  const removeBodyBuilderRow = (rowId: string) => {
    setBodyBuilderRows(prev => prev.filter(row => row.rowId !== rowId));
    setBodyBuilderError(null);
  };

  const addFormBodyRow = () => {
    setFormBodyRows(prev => [...prev, { rowId: uid(), key: '', value: '' }]);
  };

  const updateFormBodyRow = (rowId: string, field: 'key' | 'value', value: string) => {
    setFormBodyRows(prev => prev.map(row => row.rowId === rowId ? { ...row, [field]: value } : row));
    setLiveResult(null);
  };

  const removeFormBodyRow = (rowId: string) => {
    setFormBodyRows(prev => prev.filter(row => row.rowId !== rowId));
    setLiveResult(null);
  };

  const addRequestMappingRow = () => {
    setRequestMappingRows(prev => [...prev, { rowId: uid(), path: '', expression: '' }]);
    setRequestMappingBuilderActive(true);
    setRequestMappingBuilderError(null);
  };

  const updateRequestMappingRow = (rowId: string, field: 'path' | 'expression', value: string) => {
    setRequestMappingRows(prev => prev.map(row => row.rowId === rowId ? { ...row, [field]: value } : row));
    setRequestMappingBuilderActive(true);
    setRequestMappingBuilderError(null);
  };

  const removeRequestMappingRow = (rowId: string) => {
    setRequestMappingRows(prev => prev.filter(row => row.rowId !== rowId));
    setRequestMappingBuilderActive(true);
    setRequestMappingBuilderError(null);
  };

  const upsertRequestMappingRowForPath = (path: string, expression: string) => {
    const targetPath = path.trim();
    if (!targetPath) return;
    setRequestMappingRows(prev => {
      const existing = prev.find(row => row.path.trim() === targetPath);
      if (existing) {
        return prev.map(row => row.rowId === existing.rowId ? { ...row, path: targetPath, expression } : row);
      }
      return [...prev, { rowId: uid(), path: targetPath, expression }];
    });
    setRequestMappingBuilderActive(true);
    setRequestMappingBuilderError(null);
  };

  const clearRequestMappingRowForPath = (path: string) => {
    const targetPath = path.trim();
    if (!targetPath) return;
    setRequestMappingRows(prev => prev.filter(row => row.path.trim() !== targetPath));
    setRequestMappingBuilderActive(true);
    setRequestMappingBuilderError(null);
  };

  const applyBodyBuilder = () => {
    const built = buildJsonFromRows(bodyBuilderRows);
    if (!built.ok) {
      setBodyBuilderError(built.error);
      return;
    }
    setBodyBuilderError(null);
    setShowBody(true);
    setBodyText(JSON.stringify(built.value, null, 2));
    setLiveResult(null);
  };

  const applyRequestMappingBuilder = () => {
    const built = buildRequestMappingFromRows(requestMappingRows);
    if (!built.ok) {
      setRequestMappingBuilderError(built.error);
      return;
    }
    setRequestMappingBuilderError(null);
    setRequestMappingText(built.value);
    setRequestMappingValidation({ ok: false, pending: true });
    setRequestMappingEvaluationNonce(prev => prev + 1);
  };

  const handleRequestMappingViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextMode: 'fields' | 'jsonata' | null,
  ) => {
    if (!nextMode || nextMode === requestMappingViewMode) return;
    if (nextMode === 'jsonata') {
      const hasStartedRow = requestMappingRows.some(row => row.path.trim() || row.expression.trim());
      if (hasStartedRow && !requestMappingText.trim()) {
        applyRequestMappingBuilder();
      }
    }
    setRequestMappingViewMode(nextMode);
  };

  const applyRuntimeMappingTemplate = () => {
    if (!runtimeMappingTemplate) return;
    setRequestMappingBuilderActive(false);
    setRequestMappingBuilderError(null);
    setRequestMappingText(runtimeMappingTemplate);
    setRequestMappingValidation({ ok: false, pending: true });
    setRequestMappingEvaluationNonce(prev => prev + 1);
  };

  const clearRuntimeMapping = () => {
    setRequestMappingRows([]);
    setRequestMappingBuilderActive(false);
    setRequestMappingText('');
    setRequestMappingBuilderError(null);
    setRequestMappingValidation({ ok: true });
  };

  const resetRuntimeMappingSample = () => {
    setRequestMappingSampleText(defaultRuntimeSamplePayloadText(selectedCapability, providerVariables));
    if (requestMappingText.trim()) {
      setRequestMappingValidation({ ok: false, pending: true });
      setRequestMappingEvaluationNonce(prev => prev + 1);
    }
  };

  const formatRequestBody = () => {
    const parsed = parseJson(bodyText);
    if (parsed.ok) setBodyText(JSON.stringify(parsed.value, null, 2));
  };

  const formatRuntimeMappingPreviewSample = () => {
    const parsed = parseJson(requestMappingSampleText);
    if (parsed.ok) {
      setRequestMappingSampleText(JSON.stringify(parsed.value, null, 2));
      if (requestMappingText.trim()) {
        setRequestMappingValidation({ ok: false, pending: true });
        setRequestMappingEvaluationNonce(prev => prev + 1);
      }
    }
  };

  const clearRequestBody = () => {
    setBodyText('{\n  \n}');
    setFormBodyRows([]);
    setLiveResult(null);
  };

  const selectedHeaders = headersWithBodyContentType(
    stripManagedOauthHeaders(authType, buildAllHeaders(authType, authConfig, headerRows)),
    showBody,
    bodyMode,
  );
  const ignoredOauthAuthorizationHeader = authType === 'oauth-client-credentials' &&
    headerRows.some(row => row.key.trim().toLowerCase() === 'authorization');
  const liveUrl = useMemo(() => {
    const base = baseUrl.trim().replace(/\/+$/, '');
    const path = substituteProviderVariables(requestPath.trim(), providerVariables);
    if (!path) return base;
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${base}${normalized}`;
  }, [baseUrl, requestPath, providerVariables]);
  const requestPreviewJson = {
    url: liveUrl,
    method: requestMethod,
    headers: selectedHeaders,
    ...(showBody && selectedBody.ok ? { bodyType: bodyMode, body: bodyPreview } : {}),
  };
  // Display-only copy (#121): the live test sends selectedHeaders / liveUrl / selectedBody unmasked.
  const previewSecretValues = collectPreviewSecretValues(
    authConfig,
    providerVariables,
    [MASKED_SECRET_VALUE, SECRET_DISPLAY_VALUE],
  );
  const displayRequestPreview = maskRequestPreviewForDisplay(requestPreviewJson, previewSecretValues);

  const mappedEntries = Object.entries(mappingPaths)
    .map(([field, path]) => ({
      field,
      path: path.trim(),
      expression: responseMappingExpressionForInput(path),
    }))
    .filter(entry => entry.field && entry.path && entry.expression);
  const requiredFields = selectedFields.filter(field => field.required === true);
  const missingRequiredFields = requiredFields
    .map(field => field.name)
    .filter(fieldName => !(mappingPaths[fieldName] ?? '').trim());
  const requestMappingForSave = requestMappingText.trim();
  const canSaveMappings = missingRequiredFields.length === 0 && requestMappingCanContinue;

  const responseMapping = mappedEntries.reduce<Record<string, string>>((acc, entry) => {
    acc[entry.field] = entry.expression;
    return acc;
  }, {});

  const responseFields = mappedEntries.map(entry => entry.path);
  const fieldMappings: FieldMapping[] = mappedEntries.map(entry => ({
    canonicalPath: entry.field,
    vendorPath: entry.path,
    required: requiredFields.some(field => field.name === entry.field),
    transform: entry.expression,
  }));
  const payload = mappedEntries.reduce<Record<string, unknown>>((acc, entry) => {
    acc[entry.field] = null;
    return acc;
  }, {});
  const normalizedPreview = {
    serviceName,
    capability: selectedCapability.capability,
    endpointTemplate: selectedCapability.endpointTemplate,
    ...(requestMappingForSave ? { requestMapping: requestMappingForSave } : {}),
    responseMapping,
  };
  const confirmJson = {
    serviceName,
    capability: selectedCapability.capability,
    endpointTemplate: selectedCapability.endpointTemplate,
    endpointMappings: savedEndpointMappings,
    ...(requestMappingForSave ? { requestMapping: requestMappingForSave } : {}),
    responseMapping,
  };

  const requestHeadersForSave = (): Record<string, string> =>
    headersWithBodyContentType(stripManagedOauthHeaders(authType, rowsToHeaders(headerRows)), showBody, bodyMode);

  const adapterServiceKeyForEndpoint = adapterEndpointServiceName(selectedCapability);

  const buildCurrentEndpointMapping = (
    requestBody: unknown | null,
    options?: {
      onboardingStatus?: 'draft' | 'complete';
      onboardingStep?: number;
      enabled?: boolean;
    },
  ): VendorEndpointMapping => {
    const existingEndpoint = savedEndpointMappings.find(endpoint =>
      (endpoint.serviceName === adapterServiceKeyForEndpoint || endpoint.serviceName === serviceName) &&
      endpoint.capability === selectedCapability.capability,
    );
    const endpointAuthConfig = pickAuthConfigForProfile(authConfig, authProfileKind);
    return {
      serviceName: adapterServiceKeyForEndpoint,
      capability: selectedCapability.capability,
      ...(Object.keys(endpointAuthConfig).length > 0 ? { authConfig: endpointAuthConfig } : {}),
      endpointTemplate: selectedCapability.endpointTemplate,
      requestMethod,
      requestPath: requestPath.trim(),
      ...(endpointTimeoutMs !== '' ? { timeoutMs: Number(endpointTimeoutMs) } : {}),
      ...(endpointMaxRetries !== '' ? { maxRetries: Number(endpointMaxRetries) } : {}),
      ...(endpointRetryDelayMs !== '' ? { retryDelayMs: Number(endpointRetryDelayMs) } : {}),
      ...(endpointBodyFormat ? { bodyFormat: endpointBodyFormat } : {}),
      requestHeaders: requestHeadersForSave(),
      requestBodyType: showBody ? bodyMode : undefined,
      requestBody,
      ...(requestMappingForSave ? { requestMapping: requestMappingForSave } : {}),
      ...(options?.enabled !== undefined
        ? { enabled: options.enabled }
        : existingEndpoint?.enabled !== undefined
          ? { enabled: existingEndpoint.enabled }
          : {}),
      ...(options?.onboardingStatus !== undefined
        ? {
            onboardingStatus: options.onboardingStatus,
            ...(options.onboardingStep !== undefined ? { onboardingStep: options.onboardingStep } : {}),
          }
        : existingEndpoint?.onboardingStatus
          ? {
              onboardingStatus: existingEndpoint.onboardingStatus,
              ...(existingEndpoint.onboardingStep !== undefined
                ? { onboardingStep: existingEndpoint.onboardingStep }
                : {}),
            }
          : {}),
      responseFields,
      fieldMappings,
      sampleResponse: liveResult?.responseBody ?? null,
      normalizedPreview,
      responseMapping,
      testUrl: liveUrl,
      testMethod: requestMethod,
    };
  };

  const upsertEndpointMapping = (
    mappings: VendorEndpointMapping[],
    mapping: VendorEndpointMapping,
  ): VendorEndpointMapping[] => {
    const key = `${mapping.serviceName ?? serviceName}|${mapping.capability}`;
    const next = mappings.filter(item => `${item.serviceName ?? serviceName}|${item.capability}` !== key);
    return [...next, mapping];
  };

  const getRequestBodyForSave = (): { ok: true; value: unknown | null } | { ok: false } => {
    if (!showBody) return { ok: true, value: null };
    if (bodyMode === 'form-urlencoded') {
      return { ok: true, value: buildFormObject(formBodyRows, authType, authConfig, false) };
    }
    const parsed = parseJson(bodyText);
    if (!parsed.ok) return { ok: false };
    return { ok: true, value: parsed.value };
  };

  const buildVendorRequest = (endpointMappings: VendorEndpointMapping[]) => {
    const requestBody = getRequestBodyForSave();
    if (!requestBody.ok) return null;
    return {
      vendorName: vendorName.trim() || vendorId.trim(),
      baseUrl: baseUrl.trim(),
      authType,
      authConfig,
      ...(Object.keys(providerVariables).length > 0 ? { providerVariables } : {}),
      ...saveDoctrineFields(useCatalogServicePicker, existing),
      serviceName,
      capability: selectedCapability.capability,
      endpointTemplate: selectedCapability.endpointTemplate,
      requestMethod,
      requestPath: requestPath.trim(),
      requestHeaders: requestHeadersForSave(),
      requestBodyType: showBody ? bodyMode : undefined,
      requestBody: requestBody.value,
      ...(requestMappingForSave ? { requestMapping: requestMappingForSave } : {}),
      responseFields,
      fieldMappings,
      sampleResponse: liveResult?.responseBody ?? null,
      normalizedPreview,
      responseMapping,
      endpointMappings,
      adapterConfigLabel: normalizeConfigLabel(selectedConfigLabel),
      ...(selectedCatalogGroupKey ? { preferredCatalogGroupKey: selectedCatalogGroupKey } : {}),
      payload,
      testUrl: liveUrl,
      testMethod: requestMethod,
    };
  };

  const canSaveVendorShell =
    vendorName.trim().length > 0 &&
    vendorId.trim().length > 0 &&
    baseUrl.trim().length > 0 &&
    selectedConfigLabel.trim().length > 0 &&
    (useCatalogServicePicker ? Boolean(selectedCatalogGroupKey) : serviceName.length > 0);

  const authStepReady =
    showVendorShellConnect ||
    authType === 'none' ||
    hasUsableAuthConfigForProfile(authType, authConfig, authProfileKind) ||
    hasMaskedAuthSecret(authType, authConfig);

  const canGoNextStep0 = addApiCatalogFull
    ? false
    : showVendorShellConnect
      ? canSaveVendorShell
      : canSaveVendorShell &&
        authStepReady &&
        Boolean(selectedCapability.capability);
  const canGoNextStep1 =
    requestMethod.trim().length > 0 &&
    (!showBody || selectedBody.ok) &&
    requestMappingCanContinue;
  const canGoNextStep2 = liveResult !== null;

  const isServerDraftApiFlow =
    Boolean(existing) &&
    (wizardMode === 'add-api' || endpoint?.onboardingStatus === 'draft');

  const canSaveApiDraftAtStep =
    isServerDraftApiFlow &&
    Boolean(selectedCapability.capability.trim()) &&
    (step === 0 ? authStepReady : canGoNextStep1);

  const buildDraftSaveRequest = (
    endpointMappings: VendorEndpointMapping[],
  ): UpdateVendorRequest | null => {
    if (!existing) return null;
    return {
      vendorName: vendorName.trim() || existing.vendorName || existing.vendorId,
      baseUrl: baseUrl.trim() || existing.baseUrl || '',
      authType,
      authConfig,
      ...(Object.keys(providerVariables).length > 0 ? { providerVariables } : {}),
      ...saveDoctrineFields(useCatalogServicePicker, existing),
      serviceName,
      endpointMappings,
      adapterConfigLabel: normalizeConfigLabel(selectedConfigLabel),
      ...(selectedCatalogGroupKey ? { preferredCatalogGroupKey: selectedCatalogGroupKey } : {}),
    };
  };

  const restoreConnectAuthFromCurrentEndpoint = useCallback(() => {
    if (!showPerApiAuthOnConnect) return;
    const adapterKey = adapterEndpointServiceName(selectedCapability);
    if (
      wizardMode === 'add-api' &&
      !isCapabilityClaimed(savedEndpointMappings, selectedCapability.capability, serviceName, adapterKey)
    ) {
      setAuthType('none');
      setAuthConfig({});
      return;
    }
    const currentEndpoint = findSavedEndpointMapping(
      savedEndpointMappings,
      selectedCapability.capability,
      serviceName,
      adapterKey,
    );
    const vendorAuth = existing?.authConfig ?? {};
    const mergedAuth = mergeEffectiveAuthConfig(vendorAuth, currentEndpoint?.authConfig);
    setAuthConfig(mergedAuth);
    setAuthType(inferAuthTypeFromAuthConfig(mergedAuth, authProfileKind));
  }, [
    showPerApiAuthOnConnect,
    wizardMode,
    selectedCapability,
    savedEndpointMappings,
    serviceName,
    existing?.authConfig,
    authProfileKind,
  ]);

  const handleBackToConnect = () => {
    restoreConnectAuthFromCurrentEndpoint();
    setStep(0);
  };

  const resolveDraftEndpointToDelete = (): VendorEndpointMapping | null => {
    if (endpoint?.onboardingStatus === 'draft') return endpoint;
    const adapterKey = adapterEndpointServiceName(selectedCapability);
    const match = findSavedEndpointMapping(
      savedEndpointMappings,
      selectedCapability.capability,
      serviceName,
      adapterKey,
    );
    return match?.onboardingStatus === 'draft' ? match : null;
  };

  const handleDiscardDraft = () => {
    const draftToDelete = resolveDraftEndpointToDelete();
    const deletingServerDraft = Boolean(existing && draftToDelete);
    const message = deletingServerDraft
      ? `Delete this draft API (${draftToDelete!.capability}) and return to the vendors list?`
      : 'Discard this in-progress onboarding draft and return to the vendors page?';
    if (!window.confirm(message)) return;

    skipDraftPersistUntilRef.current = Date.now() + 2500;
    skipPersistOnCloseRef.current = true;
    clearCurrentDraft();
    initSessionRef.current = null;
    draftHydratedRef.current = false;
    setResumedFromDraft(false);
    setAdapterSyncSuccessMessage(null);

    if (deletingServerDraft && existing) {
      const endpointMappings = removeEndpointMapping(savedEndpointMappings, draftToDelete!);
      const request = buildDraftSaveRequest(endpointMappings);
      if (request) {
        updateVendorForApiDraft.mutate({ vendorId: existing.vendorId, request });
      }
      return;
    }
    onClose();
  };

  const handleSaveAndContinueLater = () => {
    if (!existing || !canSaveApiDraftAtStep) return;
    const requestBody = step >= 1 ? getRequestBodyForSave() : { ok: true as const, value: null };
    if (!requestBody.ok) return;
    const endpointMappings = upsertEndpointMapping(
      savedEndpointMappings,
      buildCurrentEndpointMapping(requestBody.value, {
        onboardingStatus: 'draft',
        onboardingStep: step,
        enabled: false,
      }),
    );
    const request = buildDraftSaveRequest(endpointMappings);
    if (!request) return;
    setSavedEndpointMappings(endpointMappings);
    updateVendorForApiDraft.mutate({ vendorId: existing.vendorId, request });
  };

  const handleTestConn = () => {
    const url = baseUrl.trim();
    if (!url) return;
    setConnResult(null);
    const shouldTestIncomeTokenPreflight =
      serviceName === 'income-verification' &&
      authType === 'api-key' &&
      isTokenPreflightEnabled(authConfig);
    const connectionRequest =
      authType === 'none'
        ? {
            url: shouldTestIncomeTokenPreflight ? incomeFlinksTokenTestUrl(url, authConfig) : url,
            method: shouldTestIncomeTokenPreflight ? 'POST' : 'GET',
            headers: {},
          }
        : {
            url: shouldTestIncomeTokenPreflight ? incomeFlinksTokenTestUrl(url, authConfig) : url,
            method: shouldTestIncomeTokenPreflight ? 'POST' : 'GET',
            headers: buildAuthHeaders(authType, authConfig),
            vendorId: existing?.vendorId,
            authDraft: {
              baseUrl: baseUrl.trim(),
              authType,
              authConfig,
            },
          };
    testConn.mutate(
      connectionRequest,
      {
        onSuccess: r => setConnResult({
          ok: r.ok,
          msg: r.error
            ? `${r.error} - ${r.durationMs}ms`
            : `HTTP ${r.statusCode} ${r.statusText} - ${r.durationMs}ms`,
        }),
        onError: (err: unknown) => setConnResult({
          ok: false,
          msg: vendorTestErrorMessage(err, 'Connection failed'),
        }),
      },
    );
  };

  const handleLiveTest = () => {
    let body: unknown;
    if (showBody && requestMethod !== 'GET' && requestMethod !== 'HEAD') {
      if (!selectedBody.ok) return;
      body = selectedBody.value;
    }

    setLiveResult(null);
    liveTest.mutate(
      {
        url: liveUrl,
        method: requestMethod,
        headers: selectedHeaders,
        body,
        bodyType: showBody ? bodyMode : undefined,
        capability: selectedCapability.capability,
        // Pass the vendorId so the backend can auto-inject the OAuth token for this vendor.
        vendorId: existing?.vendorId,
        authDraft: {
          baseUrl: baseUrl.trim(),
          authType,
          authConfig,
        },
      },
      {
        onSuccess: r => setLiveResult(r),
        onError: (err: unknown) => setLiveResult({
          statusCode: 0,
          statusText: 'Error',
          ok: false,
          durationMs: 0,
          error: vendorTestErrorMessage(err, 'Request failed'),
          responseBody: null,
        }),
      },
    );
  };

  const handleConfirmRequestJson = () => {
    setRequestConfirmOpen(false);
    setStep(2);
  };

  const dropPathOnField = (fieldName: string, path: string) => {
    const trimmed = path.trim();
    if (!trimmed) return;
    setMappingPaths(prev => ({ ...prev, [fieldName]: trimmed }));
    setDraggingPath(null);
  };

  const handleSave = () => {
    if (!canSaveMappings) return;

    const requestBody = getRequestBodyForSave();
    if (!requestBody.ok) return;
    const endpointMappings = upsertEndpointMapping(
      savedEndpointMappings,
      buildCurrentEndpointMapping(requestBody.value, {
        onboardingStatus: 'complete',
        enabled: true,
      }),
    );
    const common = buildVendorRequest(endpointMappings);
    if (!common) return;

    if (isEdit && existing) {
      const request: UpdateVendorRequest = common;
      updateVendor.mutate({ vendorId: existing.vendorId, request });
    } else {
      const request: CreateVendorRequest = { vendorId: vendorId.trim(), ...common };
      createVendor.mutate(request);
    }
  };

  const handleSaveVendorSettings = () => {
    if (!isEdit || !existing || !canSaveVendorShell) return;
    const request: UpdateVendorRequest = {
      vendorName: vendorName.trim() || existing.vendorName || existing.vendorId,
      baseUrl: baseUrl.trim(),
      authType: existing.authType ?? 'none',
      authConfig: existing.authConfig ?? {},
      ...(Object.keys(providerVariables).length > 0 ? { providerVariables } : {}),
      ...saveDoctrineFields(useCatalogServicePicker, existing),
      serviceName,
      endpointMappings: savedEndpointMappings,
      adapterConfigLabel: normalizeConfigLabel(selectedConfigLabel),
      ...(selectedCatalogGroupKey ? { preferredCatalogGroupKey: selectedCatalogGroupKey } : {}),
    };
    updateVendor.mutate({ vendorId: existing.vendorId, request });
  };

  const startAddApiWizard = useCallback(() => {
    if (!existing) return;
    if (!canAddApiForVendorGroup(
      mergedCapabilityCatalog,
      serviceName,
      selectedCatalogGroupKey,
      savedEndpointMappings.length > 0 ? savedEndpointMappings : (existing.endpointMappings ?? []),
    )) {
      return;
    }
    setWizardMode('add-api');
    const addApiLabel = resolveLabelForVendor(existing, initialConfigLabel);
    setSelectedConfigLabel(addApiLabel);
    const vendorContext = {
      preferredCatalogGroupKey: existing.preferredCatalogGroupKey,
      endpointMappings: existing.endpointMappings,
      serviceName: existing.serviceName,
      capability: existing.capability,
    };
    const firstEndpoint = existing.endpointMappings?.[0];
    const { groupKey, group } = resolveVendorCatalogGroup(
      vendorContext,
      adapterCatalogItems ?? [],
      addApiLabel,
    );
    const resolvedService = resolveVendorRoutingSlug(
      vendorContext,
      adapterCatalogItems ?? [],
      mergedCapabilityCatalog,
      firstEndpoint ?? null,
      addApiLabel,
    );
    if (groupKey) setSelectedCatalogGroupKey(groupKey);
    setServiceName(group?.serviceName ?? resolvedService);
    const nextCap = firstUnclaimedCapability(
      mergedCapabilityCatalog,
      resolvedService,
      groupKey,
      existing.endpointMappings ?? [],
    );
    resetBlankCapabilityForm(nextCap);
    setStep(0);
  }, [
    adapterCatalogItems,
    existing,
    initialConfigLabel,
    mergedCapabilityCatalog,
    resetBlankCapabilityForm,
    resolveLabelForVendor,
    savedEndpointMappings,
    selectedCatalogGroupKey,
    serviceName,
  ]);

  const handleCreateVendorShell = () => {
    if (!isCreateVendorOnly || !canSaveVendorShell) return;
    const request: CreateVendorRequest = {
      vendorId: vendorId.trim(),
      vendorName: vendorName.trim() || vendorId.trim(),
      baseUrl: baseUrl.trim(),
      authType: 'none',
      authConfig: {},
      ...saveDoctrineFields(useCatalogServicePicker, null),
      serviceName,
      requestMethod: 'POST',
      requestPath: '',
      requestHeaders: {},
      responseFields: [],
      endpointMappings: [],
      adapterConfigLabel: normalizeConfigLabel(selectedConfigLabel),
      ...(selectedCatalogGroupKey ? { preferredCatalogGroupKey: selectedCatalogGroupKey } : {}),
      payload: {},
    };
    createVendor.mutate(request);
  };

  const resetForAnotherEndpoint = () => {
    const nextCap = firstUnclaimedCapability(
      mergedCapabilityCatalog,
      serviceName,
      selectedCatalogGroupKey,
      savedEndpointMappings,
    );
    resetBlankCapabilityForm(nextCap);
    setStep(1);
  };

  const handleAddAnotherEndpoint = () => {
    if (!canSaveMappings) return;
    const requestBody = getRequestBodyForSave();
    if (!requestBody.ok) return;
    const endpointMappings = upsertEndpointMapping(
      savedEndpointMappings,
      buildCurrentEndpointMapping(requestBody.value, {
        onboardingStatus: 'complete',
        enabled: true,
      }),
    );
    setSavedEndpointMappings(endpointMappings);

    if (isEdit && existing) {
      const common = buildVendorRequest(endpointMappings);
      if (common) updateVendorDraft.mutate({ vendorId: existing.vendorId, request: common });
    }

    resetForAnotherEndpoint();
  };

  const saveError = isEdit
    ? (updateVendor.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ?? updateVendor.error?.message
    : (createVendor.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ?? createVendor.error?.message;
  const saveMappingBlocker = !requestMappingCanContinue
    ? `Fix runtime request mapping: ${requestMappingValidation.error ?? 'preview is still evaluating'}`
    : missingRequiredFields.length > 0
      ? `Map fields: ${missingRequiredFields.join(', ')}`
      : '';

  const saveAndContinueLaterButton = isServerDraftApiFlow ? (
    <Button
      variant="outlined"
      onClick={handleSaveAndContinueLater}
      disabled={!canSaveApiDraftAtStep || updateVendorForApiDraft.isPending}
    >
      {updateVendorForApiDraft.isPending ? <CircularProgress size={20} /> : 'Save & continue later'}
    </Button>
  ) : null;

  const renderCapabilityMenuItem = (option: CapabilityOption) => {
    const adapterKey = adapterEndpointServiceName(option);
    const claimed = isCapabilityClaimed(savedEndpointMappings, option.capability, serviceName, adapterKey);
    const current = option.capability === selectedCapability.capability;
    const disableInAddApi = wizardMode === 'add-api' && claimed;
    const saved = claimed
      ? findSavedEndpointMapping(savedEndpointMappings, option.capability, serviceName, adapterKey)
      : undefined;
    const suffix = !claimed
      ? ''
      : wizardMode === 'add-api'
        ? saved?.onboardingStatus === 'draft'
          ? ' - Draft (use Continue)'
          : ' - Already registered'
        : current
          ? ' - Currently editing'
          : ' - Configured';

    return (
      <MenuItem key={option.capability} value={option.capability} disabled={disableInAddApi}>
        {option.label} - {option.capability}
        {suffix}
      </MenuItem>
    );
  };

  const capabilityOptionSuffix = (option: CapabilityOption) => {
    const adapterKey = adapterEndpointServiceName(option);
    const claimed = isCapabilityClaimed(savedEndpointMappings, option.capability, serviceName, adapterKey);
    const current = option.capability === selectedCapability.capability;
    if (!claimed) return '';
    if (wizardMode === 'add-api') {
      const saved = findSavedEndpointMapping(savedEndpointMappings, option.capability, serviceName, adapterKey);
      return saved?.onboardingStatus === 'draft' ? ' - Draft (use Continue)' : ' - Already registered';
    }
    return current ? ' - Currently editing' : ' - Configured';
  };

  const formatCapabilitySelectLabel = (option: CapabilityOption) =>
    `${option.label} - ${option.capability}${capabilityOptionSuffix(option)}`;

  const renderCapabilitySelectValue = (value: unknown) => {
    const option = selectableCapabilityOptions.find(o => o.capability === value) ?? selectedCapability;
    return (
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', width: '100%' }}>
        {formatCapabilitySelectLabel(option)}
      </Box>
    );
  };

  const serverDraftEndpoint = resolveDraftEndpointToDelete();
  const showDiscardDraftButton =
    Boolean(serverDraftEndpoint) ||
    hasSavedDraft ||
    (wizardMode === 'vendor' && hasMeaningfulProgress(buildDraftSnapshot()));

  const discardDraftButton = showDiscardDraftButton ? (
    <Button
      color="warning"
      onClick={handleDiscardDraft}
      disabled={updateVendorForApiDraft.isPending}
    >
      {serverDraftEndpoint ? 'Delete draft' : 'Discard draft'}
    </Button>
  ) : null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '92vh' } }}>
        <DialogTitle>{modalTitle(wizardMode, existing)}</DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {addApiCatalogFull ? (
            <Alert severity="info" sx={{ mx: 3, mt: 2 }}>
              {addApiDisabledMessage}. Close this dialog or use Continue on an existing draft API to finish in-progress work.
            </Alert>
          ) : null}
          {resumedFromDraft ? (
            <Alert severity="info" sx={{ mx: 3, mt: 2 }} onClose={() => setResumedFromDraft(false)}>
              Restored your in-progress onboarding draft. You can continue where you left off.
            </Alert>
          ) : null}
          {adapterSyncSuccessMessage ? (
            <Alert severity="success" sx={{ mx: 3, mt: 2 }}>
              {adapterSyncSuccessMessage}
            </Alert>
          ) : null}
          {vendorJsonExportWarning ? (
            <Alert
              severity="warning"
              sx={{ mx: 3, mt: 2 }}
              onClose={() => setVendorJsonExportWarning(null)}
            >
              Changes are saved in the database, but exporting <code>vendorMappings.json</code> for the
              third-party-adapter or syncing adapter config failed (the adapter may not see your changes until this is fixed):{' '}
              {vendorJsonExportWarning}
            </Alert>
          ) : null}
          {!isCreateVendorOnly ? (
            <Box sx={{ px: 3, pt: 2, pb: 1 }}>
              <Stepper activeStep={step} alternativeLabel>
                {STEPS.map(label => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>
            </Box>
          ) : null}

          {step === 0 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
              {isCreateVendorOnly ? (
                <Alert severity="info">
                  Save the vendor shell first. Then use <strong>Add API</strong> on the vendors list to configure
                  each capability (Connect → Request → Live Test → Response Mapping).
                </Alert>
              ) : null}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label="Vendor Name"
                  value={vendorName}
                  onChange={e => setVendorName(e.target.value)}
                  placeholder="Mitek"
                  helperText={isEdit ? `Internal ID: ${vendorId}` : vendorId ? `Internal ID will be ${vendorId}` : 'Enter a provider name.'}
                  fullWidth
                />
                <TextField
                  label="Base URL"
                  value={baseUrl}
                  onChange={e => {
                    setBaseUrl(e.target.value);
                    setConnResult(null);
                    setLiveResult(null);
                  }}
                  placeholder="https://api.vendor.com"
                  fullWidth
                />
                <ConfigLabelSelect
                  selectOnly
                  value={selectedConfigLabel}
                  onChange={updateConfigLabel}
                />
                {!selectedConfigLabel.trim() ? (
                  <Alert severity="info" sx={{ gridColumn: { md: '1 / -1' } }}>
                    Select an adapter config label first
                  </Alert>
                ) : useCatalogServicePicker ? (
                  <FormControl fullWidth>
                    <InputLabel>Adapter service</InputLabel>
                    <Select
                      value={selectedCatalogGroupKey ?? ''}
                      label="Adapter service"
                      onChange={e => updateCatalogGroup(e.target.value)}
                    >
                      {catalogGroupsForLabel.map(group => (
                        <MenuItem key={group.key} value={group.key}>
                          {group.serviceDisplayName} ({group.adapterServiceKey})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (adapterCatalogItems?.length ?? 0) > 0 ? (
                  <Alert severity="info" sx={{ gridColumn: { md: '1 / -1' } }}>
                    No published services for label &quot;{selectedConfigLabel}&quot;. Choose another label or publish
                    Service Onboarding under this label.
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ gridColumn: { md: '1 / -1' } }}>
                    No published adapter services are available. Publish a service in Service Onboarding first, then
                    select its config label here.
                  </Alert>
                )}
              </Box>

              {showPerApiAuthOnConnect && (
                <>
                  {catalogContextBanner && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {catalogContextBanner}
                    </Alert>
                  )}
                  <FormControl fullWidth>
                    <InputLabel>Service Endpoint / Capability</InputLabel>
                    <Select
                      value={selectedCapability.capability}
                      label="Service Endpoint / Capability"
                      onChange={e => updateCapability(e.target.value)}
                    >
                      {selectableCapabilityOptions.map(renderCapabilityMenuItem)}
                    </Select>
                  </FormControl>
                  <CapabilityAuthConnectPanel
                    authType={authType}
                    authConfig={authConfig}
                    authProfileKind={authProfileKind}
                    onAuthTypeChange={next => {
                      setAuthType(next);
                      setConnResult(null);
                      if (authProfileKind === 'accounts') setAuthType('api-key');
                    }}
                    onAuthChange={(field, value) =>
                      setAuth(field, typeof value === 'boolean' ? (value ? 'true' : 'false') : value)
                    }
                    secretInputValue={secretInputValue}
                    secretInputType={secretInputType}
                    isMaskedSecretValue={isMaskedSecretValue}
                    isTokenPreflightEnabled={isTokenPreflightEnabled}
                  />
                </>
              )}

              {!showVendorShellConnect && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2">Provider variables</Typography>
                <Typography variant="caption" color="text.secondary">
                  Vendor-wide values used in JSONata as <code>c.key</code>, in URLs as <code>{'{{key}}'}</code> or{' '}
                  <code>{'{key}'}</code>, and synced to adapter_configs on save. Saving onboarding syncs adapter config
                  for label &quot;{selectedConfigLabel}&quot;.
                </Typography>
                {providerVariableRows.map(row => (
                  <Box
                    key={row.rowId}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1.5fr auto' },
                      gap: 1,
                      alignItems: 'start',
                    }}
                  >
                    <TextField
                      label="Key"
                      size="small"
                      value={row.key}
                      onChange={e => {
                        markProviderVariablesTouched();
                        const next = e.target.value;
                        setProviderVariableRows(prev =>
                          prev.map(r => (r.rowId === row.rowId ? { ...r, key: next } : r)),
                        );
                        setProviderVariableKeyError(validateProviderVariableKey(next));
                      }}
                      placeholder="model"
                    />
                    <TextField
                      label="Value"
                      size="small"
                      value={row.value}
                      onChange={e => {
                        markProviderVariablesTouched();
                        const next = e.target.value;
                        setProviderVariableRows(prev =>
                          prev.map(r => (r.rowId === row.rowId ? { ...r, value: next } : r)),
                        );
                      }}
                      placeholder="gemini-2.5-flash"
                    />
                    <IconButton
                      type="button"
                      size="small"
                      color="error"
                      aria-label="Remove variable"
                      onClick={() => {
                        markProviderVariablesTouched();
                        setProviderVariableRows(prev => prev.filter(r => r.rowId !== row.rowId));
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {providerVariableKeyError ? (
                  <Alert severity="warning">{providerVariableKeyError}</Alert>
                ) : null}
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    markProviderVariablesTouched();
                    setProviderVariableRows(prev => [
                      ...prev,
                      { rowId: `pv-${Date.now()}`, key: '', value: '' },
                    ]);
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add variable
                </Button>
              </Box>
              )}

              {!showVendorShellConnect && (
              <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {canLiveTest ? (
                  <Button
                    variant="outlined"
                    onClick={handleTestConn}
                    disabled={!baseUrl.trim() || testConn.isPending}
                  >
                    {testConn.isPending ? <CircularProgress size={18} /> : 'Test Connection'}
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Connection tests need the “Run live vendor tests” permission.
                  </Typography>
                )}
                {connResult && (
                  <Alert severity={connResult.ok ? 'success' : 'error'} sx={{ flex: 1, py: 0.25 }}>
                    {connResult.msg}
                  </Alert>
                )}
              </Box>
              {authType !== 'none' && authType !== 'oauth-client-credentials' && (
                <Alert severity="info">
                  Connection test is optional. Many providers do not support a meaningful request on the API base URL;
                  you can continue once auth fields are filled.
                </Alert>
              )}
              {authType === 'oauth-client-credentials' && (
                <Alert severity="info">
                  OAuth credentials are applied to form-url-encoded token requests without saving the client secret in request JSON.
                </Alert>
              )}
              </>
              )}
              {saveError && isCreateVendorOnly ? <Alert severity="error">{saveError}</Alert> : null}
            </Box>
          )}

          {step === 1 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
              {catalogContextBanner && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {catalogContextBanner}
                </Alert>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0, ...COMPACT_SELECT_SX }}>
                    <InputLabel>Service Endpoint / Capability</InputLabel>
                    <Select
                      value={selectedCapability.capability}
                      label="Service Endpoint / Capability"
                      onChange={e => updateCapability(e.target.value)}
                      renderValue={renderCapabilitySelectValue}
                    >
                      {selectableCapabilityOptions.map(renderCapabilityMenuItem)}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <TextField
                    label="Endpoint Template"
                    size="small"
                    value={selectedCapability.endpointTemplate}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0, ...COMPACT_SELECT_SX }}>
                    <InputLabel>Method</InputLabel>
                    <Select value={requestMethod} label="Method" onChange={e => { setRequestMethod(e.target.value); setLiveResult(null); }}>
                      {HTTP_METHODS.map(method => <MenuItem key={method} value={method}>{method}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <TextField
                    label="Vendor Request Path"
                    size="small"
                    value={requestPath}
                    onChange={e => {
                      setRequestPath(e.target.value);
                      setLiveResult(null);
                    }}
                    placeholder="/bank/verify"
                    fullWidth
                    required
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <TextField
                    label="Timeout (ms)"
                    size="small"
                    type="number"
                    value={endpointTimeoutMs}
                    onChange={e => setEndpointTimeoutMs(e.target.value === '' ? '' : Number(e.target.value))}
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <TextField
                    label="Max retries"
                    size="small"
                    type="number"
                    value={endpointMaxRetries}
                    onChange={e => setEndpointMaxRetries(e.target.value === '' ? '' : Number(e.target.value))}
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <TextField
                    label="Retry delay (ms)"
                    size="small"
                    type="number"
                    value={endpointRetryDelayMs}
                    onChange={e => setEndpointRetryDelayMs(e.target.value === '' ? '' : Number(e.target.value))}
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0, ...COMPACT_SELECT_SX }}>
                    <InputLabel>Body format</InputLabel>
                    <Select
                      label="Body format"
                      value={endpointBodyFormat}
                      onChange={e => setEndpointBodyFormat((e.target.value || '') as RequestBodyType | '')}
                    >
                      <MenuItem value="">Default (json)</MenuItem>
                      <MenuItem value="json">json</MenuItem>
                      <MenuItem value="form-urlencoded">form-urlencoded</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Request Headers</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addHeaderRow}>Add Header</Button>
                </Box>
                {headerRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Auth-derived headers are included automatically in the preview.</Typography>
                )}
                {headerRows.map(row => (
                  <Box key={row.rowId} sx={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 0.8fr) minmax(220px, 1.2fr) auto', gap: 1, mb: 1 }}>
                    <TextField label="Header" size="small" value={row.key} onChange={e => updateHeaderRow(row.rowId, 'key', e.target.value)} />
                    <TextField label="Value" size="small" value={row.value} onChange={e => updateHeaderRow(row.rowId, 'value', e.target.value)} />
                    <IconButton size="small" color="error" onClick={() => removeHeaderRow(row.rowId)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {ignoredOauthAuthorizationHeader && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Authorization is managed by OAuth for this vendor and will be injected during live test.
                  </Alert>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>Live Test Body</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Used only by the Admin Live Test button. Runtime app flows use the mapping section below.
                </Typography>
                <FormControlLabel
                  control={<Checkbox checked={showBody} onChange={e => { setShowBody(e.target.checked); setLiveResult(null); }} />}
                  label="Include request body"
                />
                {showBody && (
                  <>
                    <Box sx={{ mb: 1.5, maxWidth: 320 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Body Type</InputLabel>
                        <Select
                          value={bodyMode}
                          label="Body Type"
                          onChange={e => {
                            setBodyMode(e.target.value as RequestBodyType);
                            setLiveResult(null);
                          }}
                        >
                          <MenuItem value="json">JSON</MenuItem>
                          <MenuItem value="form-urlencoded">Form URL Encoded</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {bodyMode === 'json' && (
                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>JSON Body Builder</Typography>
                        <Button size="small" startIcon={<AddIcon />} onClick={addBodyBuilderRow}>Add Field</Button>
                      </Box>
                      {bodyBuilderRows.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Add path/value fields or paste JSON directly below.
                        </Typography>
                      )}
                      {bodyBuilderRows.map(row => (
                        <Box key={row.rowId} sx={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) minmax(160px, 1fr) auto', gap: 1, mb: 1 }}>
                          <TextField label="JSON Path" size="small" value={row.path} onChange={e => updateBodyBuilderRow(row.rowId, 'path', e.target.value)} placeholder="customer.ssn" />
                          <TextField label="Value" size="small" value={row.value} onChange={e => updateBodyBuilderRow(row.rowId, 'value', e.target.value)} placeholder="123456789" />
                          <IconButton size="small" color="error" onClick={() => removeBodyBuilderRow(row.rowId)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button size="small" variant="contained" startIcon={<DataObjectIcon />} onClick={applyBodyBuilder} disabled={bodyBuilderRows.length === 0}>
                          Generate Body JSON
                        </Button>
                        {bodyBuilderError && <Typography variant="caption" color="error">{bodyBuilderError}</Typography>}
                      </Box>
                    </Box>
                    )}

                    {bodyMode === 'form-urlencoded' && (
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Form Fields</Typography>
                          <Button size="small" startIcon={<AddIcon />} onClick={addFormBodyRow}>Add Field</Button>
                        </Box>
                        {authType === 'oauth-client-credentials' && (
                          <Alert severity="info" sx={{ mb: 1 }}>
                            <code>grant_type</code>, <code>client_id</code>, <code>client_secret</code>, and <code>scope</code> are generated from OAuth credentials. The secret is masked in preview and saved as <code>{'{{auth.clientSecret}}'}</code>.
                          </Alert>
                        )}
                        {formBodyRows.length === 0 && authType !== 'oauth-client-credentials' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Add key/value pairs for an application/x-www-form-urlencoded body.
                          </Typography>
                        )}
                        {formBodyRows.map(row => (
                          <Box key={row.rowId} sx={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) minmax(160px, 1fr) auto', gap: 1, mb: 1 }}>
                            <TextField label="Field" size="small" value={row.key} onChange={e => updateFormBodyRow(row.rowId, 'key', e.target.value)} placeholder="grant_type" />
                            <TextField label="Value" size="small" value={row.value} onChange={e => updateFormBodyRow(row.rowId, 'value', e.target.value)} placeholder="client_credentials" />
                            <IconButton size="small" color="error" onClick={() => removeFormBodyRow(row.rowId)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {bodyMode === 'json' && (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mb: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<DataObjectIcon />} onClick={formatRequestBody} disabled={!selectedBody.ok}>Format JSON</Button>
                      <Button size="small" variant="outlined" color="inherit" startIcon={<ClearIcon />} onClick={clearRequestBody}>Clear</Button>
                    </Box>
                    )}
                    {bodyMode === 'json' && (
                    <Box sx={{ border: '1px solid', borderColor: selectedBody.ok ? 'divider' : 'error.main', borderRadius: 1, overflow: 'hidden' }}>
                      <Editor
                        height="240px"
                        language="json"
                        value={bodyText}
                        theme={isDark ? 'vs-dark' : 'light'}
                        onChange={value => {
                          setBodyText(value ?? '');
                          setLiveResult(null);
                        }}
                        options={{ minimap: { enabled: false }, fontSize: 13, lineNumbers: 'on', scrollBeyondLastLine: false, tabSize: 2 }}
                      />
                    </Box>
                    )}
                    {!selectedBody.ok && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        Request body must be valid JSON before you continue: {selectedBody.error}
                      </Alert>
                    )}
                  </>
                )}
              </Box>

              <Divider />

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Runtime Request Mapping</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used only by real adapter calls. Leave empty to use the adapter default/pass-through mapping.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
                    {runtimeMappingTemplate && (
                      <Button size="small" variant="outlined" onClick={applyRuntimeMappingTemplate}>
                        Use Capability Template
                      </Button>
                    )}
                    <Button size="small" variant="outlined" color="inherit" startIcon={<ClearIcon />} onClick={clearRuntimeMapping}>
                      Clear
                    </Button>
                  </Box>
                </Box>

                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Live Test still sends the static body above. Runtime mapping evaluates JSONata against the adapter payload during app flows.
                  Map vendor fields to p.* targets from Service Onboarding; optional targets can be left unmapped.
                </Alert>

                {adapterCatalogMissingFields && (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    No published request/response catalog for this capability. Save and publish the capability in Service Onboarding first.
                  </Alert>
                )}

                {selectedCapability.canonicalRequest &&
                  Object.keys(selectedCapability.canonicalRequest).length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Button size="small" variant="text" onClick={() => setShowCanonicalRequest(v => !v)}>
                      {showCanonicalRequest ? 'Hide' : 'Show'} expected canonical request (from Service Onboarding)
                    </Button>
                    <Collapse in={showCanonicalRequest}>
                      <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                        <Editor
                          height="160px"
                          language="json"
                          value={JSON.stringify(selectedCapability.canonicalRequest, null, 2)}
                          theme={isDark ? 'vs-dark' : 'light'}
                          options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12, lineNumbers: 'off', scrollBeyondLastLine: false }}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                )}

                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Runtime Adapter Fields</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Fields available to this adapter step during real app flows.
                          </Typography>
                        </Box>
                        <Chip size="small" variant="outlined" label={`${runtimeRequestFields.length} fields`} />
                      </Box>
                      <Box sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        p: 1,
                        ...adaptiveScrollPanelSx(REQUEST_MAPPING_PANEL_HEIGHT),
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {runtimeRequestFields.map(field => {
                            const expression = jsonataExpressionForPayloadPath(field.path);
                            return (
                              <Box
                                key={`${selectedCapability.capability}:${field.path}`}
                                draggable
                                onDragStart={event => {
                                  setDraggingPath(field.path);
                                  event.dataTransfer.setData('text/plain', field.path);
                                  event.dataTransfer.effectAllowed = 'copy';
                                }}
                                onDragEnd={() => setDraggingPath(null)}
                                sx={{
                                  p: 1,
                                  border: '1px solid',
                                  borderColor: draggingPath === field.path ? 'primary.main' : 'divider',
                                  borderRadius: 1,
                                  bgcolor: draggingPath === field.path ? 'action.selected' : 'background.paper',
                                  cursor: 'grab',
                                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                                }}
                                title={`Drag ${expression}`}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{field.label}</Typography>
                                  {field.required ? (
                                    <Chip size="small" color="primary" variant="outlined" label="Required" />
                                  ) : (
                                    <Chip size="small" variant="outlined" label="Optional — skip if not provided" />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35 }}>
                                  {field.description}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ fontFamily: 'monospace', color: 'primary.main', overflowWrap: 'anywhere' }}
                                >
                                  {expression}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Vendor Request Fields</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Generated from the Live Test Body. Drop runtime adapter fields onto the vendor fields they should fill.
                        </Typography>
                      </Box>

                      <Box>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={requestMappingViewMode}
                          onChange={handleRequestMappingViewModeChange}
                        >
                          <ToggleButton value="fields">Field mapping</ToggleButton>
                          <ToggleButton value="jsonata">Advanced JSONata</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Switch between mapping vendor fields visually or viewing/editing the generated JSONata expression.
                        </Typography>
                      </Box>

                      {requestMappingViewMode === 'fields' && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          <Button size="small" startIcon={<AddIcon />} onClick={addRequestMappingRow}>Add Vendor Field</Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DataObjectIcon />}
                            onClick={applyRequestMappingBuilder}
                            disabled={requestMappingRows.length === 0}
                          >
                            Rebuild JSONata
                          </Button>
                        </Box>
                      )}

                      {requestMappingViewMode === 'fields' && requestMappingTargetPaths.length === 0 && (
                        <Alert severity="warning">
                          No vendor fields were detected from the Live Test Body. Add fields manually, or enter a valid JSON body above.
                        </Alert>
                      )}

                      {requestMappingViewMode === 'fields' ? (
                        <Box sx={{
                          ...adaptiveScrollPanelSx(REQUEST_MAPPING_PANEL_HEIGHT),
                          pr: 0.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                        }}>
                          {requestMappingDisplayRows.length === 0 && (
                            <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                              <Typography variant="body2" color="text.secondary">
                                Add a vendor field to start mapping, or switch to Advanced JSONata above.
                              </Typography>
                            </Box>
                          )}
                          {requestMappingDisplayRows.map(row => (
                            <Box
                              key={row.rowId}
                              onDragOver={event => {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = 'copy';
                              }}
                              onDrop={event => {
                                event.preventDefault();
                                const sourcePath = event.dataTransfer.getData('text/plain');
                                upsertRequestMappingRowForPath(row.path, jsonataExpressionForPayloadPath(sourcePath));
                                setDraggingPath(null);
                              }}
                              sx={{
                                p: 1.25,
                                border: '1px dashed',
                                borderColor: draggingPath ? 'primary.main' : row.mapped ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                bgcolor: draggingPath ? 'action.hover' : 'background.paper',
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'minmax(160px, 0.8fr) minmax(220px, 1.1fr) auto' },
                                gap: 1,
                                alignItems: 'center',
                              }}
                            >
                              <TextField
                                label="Vendor field"
                                size="small"
                                value={row.path}
                                onChange={e => updateRequestMappingRow(row.rowId, 'path', e.target.value)}
                                placeholder="username"
                                InputProps={{ readOnly: row.fromBody }}
                                helperText={row.fromBody ? 'From Live Test Body' : 'Manual field'}
                              />
                              <TextField
                                label="Mapped from"
                                size="small"
                                value={row.expression}
                                onChange={e => {
                                  if (row.fromBody) {
                                    upsertRequestMappingRowForPath(row.path, e.target.value);
                                  } else {
                                    updateRequestMappingRow(row.rowId, 'expression', e.target.value);
                                  }
                                }}
                                placeholder="p.credentials.username"
                                helperText="Drag a field here or type JSONata"
                              />
                              <Tooltip title={row.fromBody ? 'Clear this mapping' : 'Remove this field'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    disabled={row.fromBody && !row.mapped}
                                    onClick={() => {
                                      if (row.fromBody) {
                                        clearRequestMappingRowForPath(row.path);
                                      } else {
                                        removeRequestMappingRow(row.rowId);
                                      }
                                    }}
                                  >
                                    {row.fromBody ? <ClearIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            ...adaptiveScrollPanelSx(REQUEST_MAPPING_PANEL_HEIGHT),
                          }}>
                            <Box sx={{
                              border: '1px solid',
                              borderColor: requestMappingValidation.pending ? 'info.main' : requestMappingCanContinue ? 'divider' : 'error.main',
                              borderRadius: 1,
                              overflow: 'hidden',
                              flex: 1,
                              minHeight: 0,
                            }}>
                              <Editor
                                height={`${REQUEST_MAPPING_PANEL_HEIGHT}px`}
                                language="javascript"
                                value={requestMappingText}
                                theme={isDark ? 'vs-dark' : 'light'}
                                onChange={value => {
                                  const nextValue = value ?? '';
                                  if (nextValue === requestMappingText) return;
                                  setRequestMappingBuilderActive(false);
                                  setRequestMappingBuilderError(null);
                                  setRequestMappingText(nextValue);
                                  setRequestMappingValidation(nextValue.trim() ? { ok: false, pending: true } : { ok: true });
                                }}
                                options={{
                                  minimap: { enabled: false },
                                  fontSize: 13,
                                  lineNumbers: 'on',
                                  scrollBeyondLastLine: false,
                                  tabSize: 2,
                                  wordWrap: 'on',
                                  wrappingIndent: 'same',
                                }}
                              />
                            </Box>
                          </Box>
                          {requestMappingText.trim() && requestMappingValidation.pending && (
                            <Alert severity="info">
                              Evaluating runtime request mapping preview...
                            </Alert>
                          )}
                          {requestMappingText.trim() && !requestMappingValidation.pending && !requestMappingCanContinue && (
                            <Alert severity="error">
                              Runtime request mapping is invalid: {requestMappingValidation.error ?? 'Unable to evaluate mapping.'}
                            </Alert>
                          )}
                          {requestMappingText.trim() && requestMappingValidation.ok && !requestMappingValidation.pending && (
                            <Alert severity="success">
                              Runtime request mapping evaluates successfully.
                            </Alert>
                          )}
                        </Box>
                      )}

                      {requestMappingViewMode === 'fields' && requestMappingBuilderError && (
                        <Alert severity="error">{requestMappingBuilderError}</Alert>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 1.5, alignItems: 'stretch' }}>
                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 32 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Preview Sample Adapter Payload
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
                        <Button size="small" variant="outlined" onClick={formatRuntimeMappingPreviewSample}>Format</Button>
                        <Button size="small" variant="outlined" color="inherit" onClick={resetRuntimeMappingSample}>Reset</Button>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      This is the payload sent from backend.
                    </Typography>
                    <Box sx={{
                      border: '1px solid',
                      borderColor: requestMappingSampleJson.ok ? 'divider' : 'error.main',
                      borderRadius: 1,
                      overflow: 'hidden',
                      height: REQUEST_PREVIEW_EDITOR_HEIGHT,
                      minHeight: REQUEST_PREVIEW_EDITOR_HEIGHT,
                      maxHeight: REQUEST_PREVIEW_EDITOR_HEIGHT,
                    }}>
                      <Editor
                        height={REQUEST_PREVIEW_EDITOR_HEIGHT}
                        language="json"
                        value={requestMappingSampleText}
                        theme={isDark ? 'vs-dark' : 'light'}
                        onChange={value => {
                          const nextValue = value ?? '';
                          if (nextValue === requestMappingSampleText) return;
                          setRequestMappingSampleText(nextValue);
                          if (requestMappingText.trim()) {
                            setRequestMappingValidation({ ok: false, pending: true });
                          }
                        }}
                        options={{ minimap: { enabled: false }, fontSize: 12, lineNumbers: 'on', scrollBeyondLastLine: false, tabSize: 2 }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, minHeight: 32, display: 'flex', alignItems: 'center' }}>
                      Runtime Mapping Preview
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This is the payload after transforming using JSONata.
                    </Typography>
                    <Box sx={{
                      border: '1px solid',
                      borderColor: requestMappingValidation.pending ? 'info.main' : requestMappingValidation.ok ? 'divider' : 'error.main',
                      borderRadius: 1,
                      overflow: 'hidden',
                      height: REQUEST_PREVIEW_EDITOR_HEIGHT,
                      minHeight: REQUEST_PREVIEW_EDITOR_HEIGHT,
                      maxHeight: REQUEST_PREVIEW_EDITOR_HEIGHT,
                    }}>
                      <Editor
                        height={REQUEST_PREVIEW_EDITOR_HEIGHT}
                        language="json"
                        value={requestMappingText.trim()
                          ? JSON.stringify(
                            requestMappingValidation.pending
                              ? { status: 'Evaluating mapping preview...' }
                              : requestMappingValidation.preview ?? { error: requestMappingValidation.error ?? 'Unable to evaluate mapping.' },
                            null,
                            2,
                          )
                          : JSON.stringify({ mode: 'default adapter mapping' }, null, 2)}
                        theme={isDark ? 'vs-dark' : 'light'}
                        options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12, lineNumbers: 'on', scrollBeyondLastLine: false, domReadOnly: true }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Live Test Request JSON</Typography>
                <Box sx={{ border: '1px solid', borderColor: selectedBody.ok ? 'divider' : 'error.main', borderRadius: 1, overflow: 'hidden' }}>
                  <Editor
                    height="220px"
                    language="json"
                    value={selectedBody.ok ? JSON.stringify(displayRequestPreview, null, 2) : JSON.stringify({ error: 'Fix the request body JSON to preview the final request.' }, null, 2)}
                    theme={isDark ? 'vs-dark' : 'light'}
                    options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12, lineNumbers: 'on', scrollBeyondLastLine: false, domReadOnly: true }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {step === 2 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto', flex: 1 }}>
              <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                  Selected Vendor Endpoint
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  <strong>{requestMethod}</strong> {displayRequestPreview.url}
                </Typography>
                {Object.keys(displayRequestPreview.headers).length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {Object.entries(displayRequestPreview.headers).map(([key, value]) => (
                      <Chip
                        key={key}
                        label={`${key}: ${value}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {canLiveTest ? (
                  <Button
                    variant="contained"
                    startIcon={liveTest.isPending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                    onClick={handleLiveTest}
                    disabled={!liveUrl || liveTest.isPending}
                  >
                    {liveTest.isPending ? 'Sending...' : 'Send Request'}
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Live tests need the “Run live vendor tests” permission.
                  </Typography>
                )}
                {liveResult && (
                  <Alert
                    severity={liveResult.ok ? 'success' : 'error'}
                    icon={liveResult.ok ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                    sx={{ flex: 1, py: 0.25 }}
                  >
                    {liveResult.ok
                      ? `HTTP ${liveResult.statusCode} ${liveResult.statusText} - ${liveResult.durationMs}ms`
                      : liveResult.statusCode
                        ? `HTTP ${liveResult.statusCode} ${liveResult.statusText} - ${liveResult.durationMs}ms`
                        : (liveResult.error ?? 'Request failed')}
                  </Alert>
                )}
              </Box>

              {liveResult?.oauth && (
                <Alert
                  severity={liveResult.oauth.tokenInjected ? 'success' : 'warning'}
                  sx={{ py: 0.5 }}
                >
                  OAuth token preflight: {liveResult.oauth.attempted ? 'attempted' : 'not attempted'}
                  {liveResult.oauth.tokenStatusCode
                    ? `, HTTP ${liveResult.oauth.tokenStatusCode} ${liveResult.oauth.tokenStatusText ?? ''}`
                    : ''}
                  {liveResult.oauth.tokenInjected ? ', bearer token injected.' : ', bearer token not injected.'}
                  {liveResult.oauth.tokenUrl ? ` Token URL: ${liveResult.oauth.tokenUrl}.` : ''}
                  {liveResult.oauth.requestedScope ? ` Scope: ${liveResult.oauth.requestedScope}.` : ' Scope: <empty>.'}
                  {liveResult.oauth.error ? ` ${liveResult.oauth.error}` : ''}
                </Alert>
              )}

              {liveResult?.preflight && (
                <Alert
                  severity={liveResult.preflight.tokenInjected ? 'success' : 'warning'}
                  sx={{ py: 0.5 }}
                >
                  Token preflight: {liveResult.preflight.attempted ? 'attempted' : 'not attempted'}
                  {liveResult.preflight.tokenStatusCode
                    ? `, HTTP ${liveResult.preflight.tokenStatusCode} ${liveResult.preflight.tokenStatusText ?? ''}`
                    : ''}
                  {liveResult.preflight.tokenInjected
                    ? `, token injected into ${liveResult.preflight.targetHeaderName ?? 'configured header'}.`
                    : ', token not injected.'}
                  {liveResult.preflight.tokenUrl ? ` Token URL: ${liveResult.preflight.tokenUrl}.` : ''}
                  {liveResult.preflight.tokenResponsePath ? ` Response field: ${liveResult.preflight.tokenResponsePath}.` : ''}
                  {liveResult.preflight.error ? ` ${liveResult.preflight.error}` : ''}
                </Alert>
              )}

              {liveTestAuthWarning(liveResult) && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {liveTestAuthWarning(liveResult)}
                </Alert>
              )}

              {liveResult ? (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', flex: 1, minHeight: 280 }}>
                  <Editor
                    height="380px"
                    language="json"
                    value={typeof liveResult.responseBody === 'string' ? liveResult.responseBody : JSON.stringify(liveResult.responseBody, null, 2)}
                    theme={isDark ? 'vs-dark' : 'light'}
                    options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12, lineNumbers: 'on', scrollBeyondLastLine: false, domReadOnly: true }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Send this vendor endpoint request. After a response is received, map its paths to the service capability fields.
                </Typography>
              )}
            </Box>
          )}

          {step === 3 && (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
              {catalogContextBanner && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {catalogContextBanner}
                </Alert>
              )}
              {adapterCatalogMissingFields && (
                <Alert severity="warning">
                  No published response catalog for this capability. Save and publish the capability in Service Onboarding first.
                </Alert>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0, ...COMPACT_SELECT_SX }}>
                    <InputLabel>Service Endpoint / Capability</InputLabel>
                    <Select
                      value={selectedCapability.capability}
                      label="Service Endpoint / Capability"
                      onChange={e => updateCapability(e.target.value)}
                      renderValue={renderCapabilitySelectValue}
                    >
                      {selectableCapabilityOptions.map(renderCapabilityMenuItem)}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
                  <TextField
                    label="Endpoint Template"
                    size="small"
                    value={selectedCapability.endpointTemplate}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
              </Box>

              <Alert severity="info">
                Drag rows from the vendor response JSON into the canonical field boxes. Use the response row paths shown in the left pane.
              </Alert>

              {selectedCapability.canonicalResponse &&
                Object.keys(selectedCapability.canonicalResponse).length > 0 && (
                <Box>
                  <Button size="small" variant="text" onClick={() => setShowCanonicalResponse(v => !v)}>
                    {showCanonicalResponse ? 'Hide' : 'Show'} expected canonical response (from Service Onboarding)
                  </Button>
                  <Collapse in={showCanonicalResponse}>
                    <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                      <Editor
                        height="160px"
                        language="json"
                        value={JSON.stringify(selectedCapability.canonicalResponse, null, 2)}
                        theme={isDark ? 'vs-dark' : 'light'}
                        options={{ readOnly: true, minimap: { enabled: false }, fontSize: 12, lineNumbers: 'off', scrollBeyondLastLine: false }}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}

              {!canSaveMappings && (
                <Alert severity="error">
                  {saveMappingBlocker || 'Complete the endpoint mapping before saving.'}
                </Alert>
              )}

              {savedEndpointMappings.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Saved Endpoint Mappings
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {savedEndpointMappings.map(endpoint => (
                      <Chip
                        key={`${endpoint.capability}:${endpoint.requestMethod}:${endpoint.requestPath}`}
                        label={`${endpoint.capability} | ${endpoint.requestMethod} ${endpoint.requestPath || endpoint.endpointTemplate || '/'}`}
                        variant="outlined"
                        color="primary"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, minHeight: 0, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: 'background.paper', py: 0.5, minHeight: RESPONSE_SECTION_HEADER_MIN_HEIGHT }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Vendor Live Response JSON
                    </Typography>
                    <Chip size="small" variant="outlined" label="Drag rows" />
                  </Box>

                  <Box sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    height: RESPONSE_PANEL_SHELL_HEIGHT,
                    minHeight: RESPONSE_PANEL_SHELL_HEIGHT,
                  }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: RESPONSE_TREE_GRID_COLUMNS, gap: 1, px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>Key</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>Value</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>Path</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 0.75, pb: 1.5 }}>
                      {liveResult?.responseBody !== null && typeof liveResult?.responseBody === 'object' ? (
                        <Box sx={{ minWidth: 'max-content' }}>
                          <JsonDraggableTree
                            key={`vendor-json-tree-${serviceName}-${selectedCapability.capability}`}
                            value={liveResult.responseBody}
                            activePath={draggingPath}
                            onDragPath={setDraggingPath}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          The live response is not a JSON object. Send a request that returns JSON before mapping fields.
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', px: 1, py: 0.75 }}>
                      {flatPaths.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No leaf paths were detected. You can still type paths manually on the right.
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {flatPaths.length} response paths available.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box
                  key={`canonical-drop-${serviceName}-${selectedCapability.capability}-${selectedCapability.schema}`}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, minHeight: 0, height: '100%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: 'background.paper', py: 0.5, minHeight: RESPONSE_SECTION_HEADER_MIN_HEIGHT }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Map Canonical Fields
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${selectedFields.length} fields · ${selectedCapability.capability}`}
                      />
                    </Box>
                  </Box>

                  <Box sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    height: RESPONSE_PANEL_SHELL_HEIGHT,
                    minHeight: RESPONSE_PANEL_SHELL_HEIGHT,
                    minWidth: 0,
                  }}>
                    <Box sx={{ px: 1, pt: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>How to map: </Box>
                        Drag a path from the vendor JSON tree on the left, or type a dotted path / JSONata expression in each field below.
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>What each field is: </Box>
                        {responseFieldDescriptionHeading(selectedFields)
                          ? responseFieldPurposeLabel(responseFieldDescriptionHeading(selectedFields)!)
                          : 'A canonical output field defined for this capability — map a vendor path to populate it in the normalized response.'}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                        gridAutoRows: 'auto',
                        alignItems: 'start',
                        alignContent: 'start',
                        gap: 2,
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        px: 1,
                        pt: 0.5,
                        pb: 1.5,
                        pr: 0.5,
                      }}
                    >
                    {selectedFields.length === 0 && (
                      <Alert severity="error">
                        No canonical fields are configured for {selectedCapability.capability}.
                      </Alert>
                    )}
                    {selectedFields.map(field => {
                      const currentPath = mappingPaths[field.name] ?? '';
                      const currentExpression = responseMappingExpressionForInput(currentPath);
                      const jsonataPreview = currentExpression ? `JSONata: ${currentExpression}` : 'JSONata: -';
                      const isRequired = field.required === true;
                      const isMissing = isRequired && !currentPath.trim();
                      const statusLabel = canonicalFieldMappingStatusLabel(currentPath, isRequired);
                      const sharedDescriptionHeading = responseFieldDescriptionHeading(selectedFields);
                      const showFieldDescription = Boolean(
                        field.description?.trim() &&
                        field.description.trim() !== sharedDescriptionHeading,
                      );
                      return (
                        <Box
                          key={`${selectedCapability.capability}-${field.name}`}
                          onDragOver={event => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'copy';
                          }}
                          onDrop={event => {
                            event.preventDefault();
                            dropPathOnField(field.name, event.dataTransfer.getData('text/plain'));
                          }}
                          sx={{
                            p: 1.5,
                            border: '1px dashed',
                            borderColor: isMissing ? 'error.main' : draggingPath ? 'primary.main' : currentPath ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            bgcolor: isMissing ? 'error.lighter' : draggingPath ? 'action.hover' : 'background.paper',
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere' }}
                            >
                              {field.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                              {isRequired ? (
                                <Chip size="small" color="error" variant="outlined" label="Required" sx={{ flexShrink: 0 }} />
                              ) : (
                                <Tooltip title="Optional — skip if not provided">
                                  <Chip size="small" variant="outlined" label="Optional" sx={{ flexShrink: 0 }} />
                                </Tooltip>
                              )}
                              <Chip
                                size="small"
                                color={currentPath ? 'primary' : isMissing ? 'warning' : 'default'}
                                variant={currentPath ? 'filled' : 'outlined'}
                                label={statusLabel}
                                sx={{ flexShrink: 0 }}
                              />
                            </Box>
                          </Box>

                          {field.name === 'rawResponse' ? (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                              <Button
                                size="small"
                                variant="text"
                                sx={{ flexShrink: 0, textTransform: 'none', fontSize: '0.7rem', px: 0 }}
                                onClick={() => setMappingPaths(prev => ({ ...prev, rawResponse: '$' }))}
                              >
                                Use full response ($)
                              </Button>
                            </Box>
                          ) : null}

                          {showFieldDescription ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                lineHeight: 1.4,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {field.description}
                            </Typography>
                          ) : null}

                          <TextField
                            size="small"
                            fullWidth
                            value={currentPath}
                            onChange={e => setMappingPaths(prev => ({ ...prev, [field.name]: e.target.value }))}
                            placeholder="Drop vendor path or type JSONata"
                            sx={{
                              flex: 1,
                              '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.75rem' },
                            }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            title={jsonataPreview}
                            sx={{
                              fontFamily: 'monospace',
                              overflowWrap: 'anywhere',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {jsonataPreview}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  </Box>
                </Box>
              </Box>

              {flatPaths.length === 0 && (
                <Alert severity="warning">
                  No response paths were detected from Live Test. You can still type paths manually, or go back and send a request that returns a JSON object.
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Generated Mapping JSON</Typography>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Editor
                    height="300px"
                    language="json"
                    value={JSON.stringify(confirmJson, null, 2)}
                    theme={isDark ? 'vs-dark' : 'light'}
                    options={{ readOnly: true, minimap: { enabled: false }, fontSize: 13, lineNumbers: 'on', scrollBeyondLastLine: false, domReadOnly: true }}
                  />
                </Box>
              </Box>

              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {step === 0 && (
            <>
              <Button onClick={onClose}>Cancel</Button>
              {discardDraftButton}
              {isCreateVendorOnly ? (
                <Button
                  variant="contained"
                  onClick={handleCreateVendorShell}
                  disabled={!canSaveVendorShell || createVendor.isPending}
                >
                  {createVendor.isPending ? <CircularProgress size={20} /> : 'Save Vendor'}
                </Button>
              ) : isEdit && wizardMode === 'vendor' ? (
                <>
                  <Tooltip title={canAddAnotherApi ? 'Configure another capability for this vendor' : addApiDisabledMessage}>
                    <span>
                      <Button variant="outlined" onClick={startAddApiWizard} disabled={!canSaveVendorShell || !canAddAnotherApi}>
                        Add API
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    onClick={handleSaveVendorSettings}
                    disabled={!canSaveVendorShell || updateVendor.isPending}
                  >
                    {updateVendor.isPending ? <CircularProgress size={20} /> : 'Save Vendor'}
                  </Button>
                </>
              ) : (
                <>
                  {saveAndContinueLaterButton}
                  <Button variant="contained" onClick={() => setStep(1)} disabled={!canGoNextStep0}>
                    Next: Request
                  </Button>
                </>
              )}
            </>
          )}
          {step === 1 && (
            <>
              <Button onClick={handleBackToConnect}>Back</Button>
              {discardDraftButton}
              {saveAndContinueLaterButton}
              <Button variant="contained" onClick={() => setRequestConfirmOpen(true)} disabled={!canGoNextStep1}>
                Next: Live Test
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button onClick={() => setStep(1)}>Back</Button>
              {discardDraftButton}
              {saveAndContinueLaterButton}
              <Button variant="contained" onClick={() => setStep(3)} disabled={!canGoNextStep2}>
                Next: Response Mapping
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button onClick={() => setStep(2)}>Back</Button>
              {discardDraftButton}
              {saveAndContinueLaterButton}
              <Tooltip title={
                !canSaveMappings
                  ? saveMappingBlocker
                  : !canAddAnotherApi
                    ? addApiDisabledMessage
                    : 'Save this endpoint mapping and configure another endpoint for the same vendor.'
              }>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleAddAnotherEndpoint}
                    disabled={
                      !canSaveMappings ||
                      !canAddAnotherApi ||
                      updateVendorDraft.isPending ||
                      (isEdit ? updateVendor.isPending : createVendor.isPending)
                    }
                  >
                    Add Another Endpoint
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={!canSaveMappings ? saveMappingBlocker : ''}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleSave}
                    disabled={!canSaveMappings || (isEdit ? updateVendor.isPending : createVendor.isPending)}
                  >
                    {(isEdit ? updateVendor.isPending : createVendor.isPending)
                      ? <CircularProgress size={20} />
                      : 'Confirm & Save'}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </DialogActions>
      </Dialog>

      <RequestJsonConfirmDialog
        open={open && requestConfirmOpen}
        requestPreview={requestPreviewJson}
        secretValues={previewSecretValues}
        isDark={isDark}
        confirmDisabled={!canGoNextStep1}
        onClose={() => setRequestConfirmOpen(false)}
        onConfirm={handleConfirmRequestJson}
      />
    </>
  );
}
